// askSmart-ui/src/pages/TOSP/types.ts
import { BudgetHistory } from "./budgetHistory";

/** A managed building. Units — and through them tenants — hang off a building. */
export interface Building {
  id: string;
  /** Full legal / display name. Required and unique across the org. */
  name: string;
  /** Optional short name, used where the full name is too long. */
  nickname?: string;
  /** Grey Standard Condominium Corporation number, e.g. "61". Optional. */
  gsccNumber?: string;
  /**
   * Common Area Maintenance expense, as typed on the old building form.
   *
   * No longer edited — the box became GSCC # — but kept on the records that
   * have one, because Avg CAM PSF still reads it until CAM comes out of the
   * budget. Absent on buildings added since.
   */
  camExpense?: number;
}

// Lookup lists an admin maintains under Configuration. They're plain strings
// rather than literal unions so a new business category never needs a code
// change — the defaults below only seed the lists on first use.
export const DEFAULT_SPACE_TYPES = [
  "Commercial",
  "Mezzanine",
  "Basement",
  "Storage",
  "Enclosed Patio",
];

export const DEFAULT_RETAILER_TYPES = ["F&B", "QSR", "Service", "Retail"];

/**
 * The management fee charged on income, as a percentage. QuickBooks can't work
 * it out — it depends on the whole budget — so TOSP calculates it and the admin
 * keys the result into account 55001 by hand.
 *
 * A constant for now because it has never varied; it belongs in Configuration
 * the day it does.
 */
export const MANAGEMENT_FEE_RATE = 4;

/** The QuickBooks account the calculated fee is keyed into. */
export const MANAGEMENT_FEE_ACCOUNT = "55001 Mgmt Fee";

/**
 * The CAM category the management fee always falls under. It is not a choice —
 * the fee is charged on the whole property, so it divides the same way every
 * time, and the line is locked to it.
 */
export const MANAGEMENT_FEE_CATEGORY = "All Cam";

/**
 * The space type waste is charged against, and the one CAM is allocated over.
 * Matched by name against the configured Space Types, so renaming that entry
 * means changing this too.
 */
export const COMMERCIAL_SPACE_TYPE = "Commercial";

/**
 * The space types whose rent is Base Rent (QuickBooks 40001). Everything else
 * that earns rent — Mezzanine, Basement, Storage — is Storage Rent (40002).
 *
 * Deliberately its own list rather than reusing COMMERCIAL_SPACE_TYPE above:
 * an enclosed patio is trading space and is billed as base rent, but it is NOT
 * part of the waste or CAM area. Widening the constant instead would have
 * silently changed both of those allocations.
 */
export const BASE_RENT_SPACE_TYPES = [COMMERCIAL_SPACE_TYPE, "Enclosed Patio"];

export const DEFAULT_WASTE_REMOVAL_TYPES = [
  "Exempt",
  "F&B Hi",
  "F&B Lo",
  "Retail Hi",
  "Retail Lo",
];

// A CAM category and how its costs are divided. The split lives here rather
// than on each budget, so it's defined once — but a budget year copies it at
// setup, so editing a category later can never re-divide a past year.
export interface CamCategory {
  id: string;
  name: string;
  split: SplitMapping;
}

// The first few only — the rest are added by an admin under Configuration.
export const DEFAULT_CAM_CATEGORY_NAMES = ["Seasons", "Grand Georgian", "Weider Lodge"];

export const SPLIT_AMONG_OPTIONS = ["Buildings", "Units", "Tenants", "Waste Removal"] as const;

export type SplitAmong = (typeof SPLIT_AMONG_OPTIONS)[number];

export const SPLIT_BASIS_OPTIONS = ["Unit Sqft", "Even Split", "% Split"] as const;

export type SplitBasis = (typeof SPLIT_BASIS_OPTIONS)[number];

/** What the Entity dropdown is called once Split Among has been chosen. */
export const splitEntityLabel = (splitAmong: SplitAmong | ""): string =>
  splitAmong === "Buildings"
    ? "Building"
    : splitAmong === "Units"
      ? "Unit"
      : splitAmong === "Tenants"
        ? "Tenant"
        : splitAmong === "Waste Removal"
          ? "Waste Removal"
          : "Entity";

// How a cost is divided: across which set of things, which one of them, and on
// what basis.
export interface SplitMapping {
  splitAmong: SplitAmong | "";
  /**
   * The entities the cost is divided across — building / unit / tenant ids, or
   * the waste removal values themselves, depending on `splitAmong`. A cost is
   * rarely split against a single one: "every waste band except Exempt" is a
   * list, so this is a list.
   */
  entities: string[];
  basedOn: SplitBasis | "";
  /** Each entity's share when basedOn is "% Split", keyed by entity value. */
  percentages: Record<string, number>;
  /**
   * Deliberately excluded — header and exempt rows carry a category so the
   * budget reads properly, but take no part in the calculations. Distinct
   * from a category whose split simply hasn't been defined yet.
   */
  ignored: boolean;
}

/** The basis that needs a percentage against every entity. */
export const PERCENT_SPLIT: SplitBasis = "% Split";

export const emptySplitMapping = (): SplitMapping => ({
  splitAmong: "",
  entities: [],
  basedOn: "",
  percentages: {},
  ignored: false,
});

export const splitPercentTotal = (mapping: SplitMapping): number =>
  (mapping.entities || []).reduce(
    (sum, entity) => sum + (mapping.percentages?.[entity] ?? 0),
    0,
  );

// A percentage split has to account for the whole cost. Compared with a small
// tolerance so 33.33 × 3 isn't rejected for floating-point noise.
export function splitPercentError(mapping: SplitMapping): string | null {
  if (mapping.ignored) return null;
  if (mapping.basedOn !== PERCENT_SPLIT) return null;
  if ((mapping.entities?.length ?? 0) === 0) return null;
  const total = splitPercentTotal(mapping);
  if (Math.abs(total - 100) < 0.01) return null;
  return `Percentages total ${Number(total.toFixed(2))}% — they must add up to 100%.`;
}

// An ignored category is complete by definition — there is nothing to divide,
// which is the point. Anything else needs a full mapping.
export const isSplitMappingComplete = (mapping: SplitMapping): boolean =>
  mapping.ignored ||
  (!!mapping.splitAmong &&
    (mapping.entities?.length ?? 0) > 0 &&
    !!mapping.basedOn &&
    splitPercentError(mapping) === null);

// Splits once held a single `entity` and no percentages. Read those forward so
// nothing already committed is stranded.
export function normalizeSplitMapping(
  mapping: SplitMapping & { entity?: string; noSplit?: boolean },
): SplitMapping {
  return {
    splitAmong: mapping.splitAmong,
    entities: Array.isArray(mapping.entities)
      ? mapping.entities
      : mapping.entity
        ? [mapping.entity]
        : [],
    basedOn: mapping.basedOn,
    percentages: mapping.percentages || {},
    ignored: mapping.ignored ?? mapping.noSplit ?? false,
  };
}

/** One line of the uploaded QBO export, once a category has been assigned. */
export interface BudgetLine {
  id: string;
  description: string;
  /** The year's figure — the twelve months summed. */
  amount: number | null;
  /** The QuickBooks account code, e.g. "51001". */
  code?: string;
  /** The twelve monthly figures as exported. */
  months?: number[];
  /**
   * Which half of the P&L the line came from. Income is imported for the
   * record but never categorised — revenue is TOSP's own Revenue Budget, and
   * CAM categories only divide costs.
   */
  section?: "income" | "expense";
  /**
   * A parent account or section from the export — "5500 Property Mgmt". Kept
   * for the display so the budget reads the way QuickBooks laid it out, but
   * carries no money: its children hold it, and counting it would double the
   * budget. Never categorised, never allocated, never totalled.
   */
  isHeader?: boolean;
  /**
   * A line TOSP produced rather than imported — the management fee. Its
   * category is fixed and can't be changed.
   */
  locked?: boolean;
  /** One of the configured CAM Categories. Blank until step 2 assigns it. */
  camCategory: string;
}

/** How one CAM category's costs are split, decided in step 3. */
export interface CategorySplit extends SplitMapping {
  camCategory: string;
}

export interface TospConfiguration {
  retailerTypes: string[];
  /** The bands a tenant is billed under, on the Tenant form. */
  wasteRemovalTypes: string[];
  /** Waste PSF Calculator: the cost being divided across the bands. */
  wasteTotalCost: number | null;
  /** Waste PSF Calculator: each band's share, keyed by waste type. */
  wasteSharePercentages: Record<string, number>;
  spaceTypes: string[];
  /**
   * BMVA Fees — the village association's own two numbers.
   *
   * These aren't negotiated per unit or per lease: BMVA sets one rate and one
   * royalty for everybody, and changes them for everybody at once. Held here so
   * that change is one edit rather than sixty, and so no unit can quietly sit
   * on last year's rate.
   */
  bmvaRatePerSqFt: number | null;
  /** Percent of gross revenue. The lease calls it the Royalty Fee. */
  bmvaRoyaltyPercentage: number | null;
  /**
   * Space Types BMVA doesn't charge the basic fee on — Storage, and whatever
   * else turns out to be exempt.
   *
   * By type rather than by unit: it's a property of the kind of space, so a
   * new storage unit is exempt the day it's created instead of the day someone
   * remembers to mark it.
   */
  bmvaExemptSpaceTypes: string[];
  /**
   * Individual units BMVA doesn't charge the basic fee on, whatever their
   * Space Type.
   *
   * The type rule covers the pattern — all Storage is exempt — and this covers
   * what a pattern can't: the one-off arrangements that don't follow from what
   * kind of space it is.
   */
  bmvaExemptUnitIds: string[];
  /**
   * Space Types percentage rent is charged on.
   *
   * A lease states one Breakpoint Rate, but it doesn't reach every space it
   * demises — percentage rent is charged on the commercial premises, not on
   * the basement, the storage or the patio. So the rate stays a lease term and
   * which spaces it reaches is a property of the kind of space, the same way
   * BMVA exemption is.
   *
   * An inclusion list rather than an exclusion one: a space type nobody has
   * thought about yet shouldn't quietly start attracting percentage rent.
   */
  percentageRentSpaceTypes: string[];
  camCategories: CamCategory[];
}

export const DEFAULT_CONFIGURATION: TospConfiguration = {
  retailerTypes: DEFAULT_RETAILER_TYPES,
  wasteRemovalTypes: DEFAULT_WASTE_REMOVAL_TYPES,
  wasteTotalCost: null,
  wasteSharePercentages: {},
  spaceTypes: DEFAULT_SPACE_TYPES,
  bmvaRatePerSqFt: null,
  bmvaRoyaltyPercentage: null,
  bmvaExemptSpaceTypes: [],
  bmvaExemptUnitIds: [],
  percentageRentSpaceTypes: ["Commercial"],
  camCategories: DEFAULT_CAM_CATEGORY_NAMES.map((name) => ({
    id: newId("cam"),
    name,
    split: emptySplitMapping(),
  })),
};

// Categories were once a plain list of names. Read those as categories with no
// split yet, so nothing an admin already committed is stranded.
export function normalizeConfiguration(
  configuration: TospConfiguration & { camCategories?: (string | CamCategory)[] },
): TospConfiguration {
  return {
    ...configuration,
    // Absent on any configuration saved before BMVA Fees moved here. Left null
    // rather than defaulted, so the fee falls back to whatever each unit and
    // occupancy already held until an admin states the real figure once.
    bmvaRatePerSqFt: configuration.bmvaRatePerSqFt ?? null,
    bmvaRoyaltyPercentage: configuration.bmvaRoyaltyPercentage ?? null,
    bmvaExemptSpaceTypes: configuration.bmvaExemptSpaceTypes ?? [],
    bmvaExemptUnitIds: configuration.bmvaExemptUnitIds ?? [],
    // Absent on any configuration saved before percentage rent became a
    // per-space-type question. Defaulted rather than left empty: an empty list
    // means "no space attracts percentage rent", which would silently zero
    // every breakpoint in the portfolio.
    percentageRentSpaceTypes: configuration.percentageRentSpaceTypes ?? ["Commercial"],
    camCategories: (configuration.camCategories || []).map((category) =>
      typeof category === "string"
        ? { id: newId("cam"), name: category, split: emptySplitMapping() }
        : { ...category, split: normalizeSplitMapping(category.split) },
    ),
  };
}

// A unit's municipal tax roll. Roll #, Plan #, Level and Unit identify the
// same assessed parcel every year, so they belong to the unit rather than
// being repeated once per tax year — every unit has exactly one, and it's
// captured on the Unit form.
export interface TaxRollIdentity {
  rollNumber: string;
  planNumber: string;
  level: string;
  unit: string;
}

export const emptyTaxRollIdentity = (): TaxRollIdentity => ({
  rollNumber: "",
  planNumber: "",
  level: "",
  unit: "",
});

// ─── The roll register ───────────────────────────────────────────────────────
//
// The municipality bills per ROLL, one page per roll, and a single unit can
// hold many: C1 is Plan 61 Level 1 Units 1–10, ten rolls and ten pages. So the
// roll is its own record rather than a field on the unit, created by ingesting
// the tax bill and then assigned — never typed.
//
// The assignment lives HERE, on the roll, not as a list on the unit. Two
// admins assigning two different rolls then write two different documents and
// can never collide; "unassigned" is a filter rather than a second list to
// keep in step; and a roll can be reassigned without rewriting the unit.

/** Where a roll's tax lands. Only units today — staff are still confirming
 *  whether lockers, parking and the land parcels ever go elsewhere, and the
 *  tagged shape means adding a destination is additive, not a migration. */
export interface TaxRollAssignment {
  type: "unit";
  /** Id of the record named by `type`. */
  id: string;
}

/**
 * One share of a roll.
 *
 * A suite that gets divided — A2-1 becoming A2-1(A) and A2-1(B) — keeps its
 * rolls: the municipality still bills one parcel, one levy. What changes is
 * that the roll now has two parts, each assignable to a different unit, and
 * the cost divides between them by floor area.
 *
 * An undivided roll has exactly one part with no label, so the common case
 * carries no extra shape.
 */
export interface TaxRollPart {
  id: string;
  /** "A", "B", "C" … blank while the roll is whole. */
  label: string;
  assignedTo: TaxRollAssignment | null;
}

/**
 * How a roll divides, from one tax year onward.
 *
 * Dated because a split must never rewrite a closed year: A2-1 was one unit
 * with one tenant through 2023 and two from 2024, and 2023's reconciliation
 * has already been settled on that basis. A year reads the newest allocation
 * dated on or before it, so splitting is an addition rather than an edit.
 */
export interface TaxRollAllocation {
  id: string;
  /** Applies to this tax year and every later one, until superseded. */
  fromYear: number;
  parts: TaxRollPart[];
}

/** Labels handed to parts in order, so a split reads A / B / C. */
const PART_LABELS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

// Only a suggestion — parts can be renamed — so it skips whatever is already
// taken rather than counting, and a renamed A doesn't cost the next split its
// letter.
export const nextPartLabel = (parts: TaxRollPart[]): string => {
  const taken = new Set(parts.map((part) => part.label.trim().toUpperCase()));
  return (
    PART_LABELS.find((label) => !taken.has(label)) || String(parts.length + 1)
  );
};

/** One assessed parcel, as the municipality bills it. */
export interface TaxRollRecord {
  id: string;
  /** The municipality's roll number — the stable key across every bill. */
  rollNumber: string;
  /** Condo plan number, digits only: the bill spells the same plan as "GREY
   *  CONDO PLAN 61", "GCP 61" and "GSCP 61" on different pages, so only the
   *  number is dependable. The spelling survives in `legalDescription`. */
  planNumber: string;
  /** "1", "A", "B" — blank on parcels that aren't in a condo plan. */
  level: string;
  /** Unit within the level. Blank on land parcels. */
  unit: string;
  /** The legal description verbatim, e.g. "GSCP 96 LEVEL A UNIT 1 LKR". */
  legalDescription: string;
  /** Municipal address as printed; some rolls genuinely have none. */
  municipalAddress: string;
  /** How the roll divides, oldest first. Always at least one entry. */
  allocations: TaxRollAllocation[];
  /** The bill it was last seen on, e.g. "2026 Interim" — how a roll that has
   *  quietly stopped being billed becomes visible instead of just going stale. */
  lastBilled: string;
  /** What this parcel was billed, one entry per bill. Nested rather than in
   *  their own container because a levy is meaningless apart from its roll and
   *  is never read without it — and writing one is still a single-roll write,
   *  so two admins working different rolls still can't collide. */
  levies: TaxLevy[];
  /**
   * Next year's expected tax, per roll share.
   *
   * Budgeted on the parcel rather than on the unit because that's where the
   * evidence is: a budget is last year's bill for THIS roll plus a percentage,
   * and a unit holding ten of them is ten separate judgements. The unit's
   * Budget PA is their sum.
   */
  budgets: TaxRollBudget[];
}

/** One roll share's budget for one year. */
export interface TaxRollBudget {
  year: number;
  /** The allocation part it belongs to — a split roll budgets each side. */
  partId: string;
  amount: number;
}

/** One bill's line for one roll. */
export interface TaxLevy {
  /** Which bill. Open rather than a boolean: Ontario also issues supplementary
   *  and omitted assessment bills mid-year — exactly what a newly built store
   *  attracts — and those need somewhere to go. */
  type: TaxBillType;
  year: number;
  /** The levy for this bill alone. Never includes arrears. */
  levy: number;
  /**
   * A prior-year balance the town has rolled onto this bill. Held apart from
   * `levy` on purpose: the bill adds it to the March instalment, and folding
   * it in would recover last year's arrears from this year's tenant.
   */
  pastDue: number;
  /** Due date (ISO) and amount, as printed. Usually two per bill; a property
   *  on Pre-Authorized Payment is withdrawn monthly instead. */
  instalments: { dueDate: string; amount: number }[];
}

export type TaxBillType = "interim" | "final" | "supplementary";

export const TAX_BILL_LABELS: Record<TaxBillType, string> = {
  interim: "Interim",
  final: "Final",
  supplementary: "Supplementary",
};

/** One roll's levy for a given bill, or undefined if it wasn't on that bill. */
export const levyOf = (
  roll: TaxRollRecord,
  year: number,
  type: TaxBillType,
): TaxLevy | undefined =>
  (roll.levies || []).find((levy) => levy.year === year && levy.type === type);

/** The town's penalty on overdue tax: 1.25% of the unpaid amount per month. */
const PENALTY_RATE = 0.0125;
/** Rounding slack when matching a past-due balance to what it's made of. */
const CENT = 0.02;

/**
 * The part of a bill's past-due balance that is a NEW cost.
 *
 * A Final's "Past Due / - Credit" is mostly not new: it's the Interim
 * instalment the tenant didn't pay, re-presented. That tax is already recorded
 * under the Interim, so charging the balance whole would bill it twice — the
 * 2025 Final's $73,593.02 is $71,798.00 of re-billed May instalment and only
 * $1,795.02 of penalty.
 *
 * Derived rather than stored, so it doesn't matter which bill is uploaded
 * first: recording the Interim later corrects the Final's split by itself.
 *
 * The unpaid amount is inferred, since a bill doesn't say what was paid. Every
 * trailing run of the year's earlier instalments is tested against the balance
 * at 1.25% a month; the one that reconciles is the principal, and the
 * remainder is penalty. Where nothing reconciles the whole balance is treated
 * as new — better to charge a tenant something we can't account for than to
 * write it off silently. Credits (a negative balance) are new by definition.
 */
export function levyPenalty(
  roll: TaxRollRecord,
  year: number,
  type: TaxBillType,
): number {
  const levy = levyOf(roll, year, type);
  if (!levy || !levy.pastDue) return 0;
  if (levy.pastDue < 0) return levy.pastDue;

  // What the year's earlier bills asked for, oldest first.
  const earlier = (roll.levies || [])
    .filter((other) => other.year === year && other.type !== type)
    .flatMap((other) => other.instalments)
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  // Trailing runs — the last instalment, the last two, and so on. Arrears run
  // backwards from the due date: the most recent instalment goes unpaid first.
  for (let take = 1; take <= earlier.length; take += 1) {
    const principal = earlier
      .slice(earlier.length - take)
      .reduce((sum, instalment) => sum + instalment.amount, 0);
    if (principal <= 0) continue;
    for (let months = 0; months <= 12; months += 1) {
      const withPenalty = principal * (1 + PENALTY_RATE * months);
      if (Math.abs(withPenalty - levy.pastDue) <= CENT) {
        return Math.round((levy.pastDue - principal) * 100) / 100;
      }
    }
  }

  return levy.pastDue;
}

/**
 * What a unit was billed for one bill — the sum across every roll it holds, so
 * C1's Interim is its ten rolls added up rather than a figure someone typed.
 *
 * null when none of its rolls carry that bill, which is distinct from a real
 * zero and lets the caller fall back to a hand-entered figure.
 */
export function unitLevyTotal(
  rolls: TaxRollRecord[],
  unitId: string,
  year: number,
  type: TaxBillType,
  units: Pick<Unit, "id" | "occupancySqFt">[],
): number | null {
  return unitShareTotal(rolls, unitId, year, units, (roll) => {
    const levy = levyOf(roll, year, type);
    return levy ? levy.levy : null;
  });
}

/**
 * The penalties and credits a unit bears for a year, across every bill.
 *
 * Kept apart from the levies because it isn't assessed tax — it's what the
 * town added for late payment — but shared out the same way, so nothing the
 * town charged goes unallocated.
 */
export function unitPenaltyTotal(
  rolls: TaxRollRecord[],
  unitId: string,
  year: number,
  units: Pick<Unit, "id" | "occupancySqFt">[],
): number | null {
  return unitShareTotal(rolls, unitId, year, units, (roll) => {
    const types = new Set((roll.levies || []).filter((l) => l.year === year).map((l) => l.type));
    if (types.size === 0) return null;
    let total = 0;
    types.forEach((type) => {
      total += levyPenalty(roll, year, type);
    });
    return total;
  });
}

/** What's budgeted for one roll share in a year, or null if nothing is. */
export const partBudgetOf = (
  roll: TaxRollRecord,
  year: number,
  partId: string,
): number | null =>
  (roll.budgets || []).find((b) => b.year === year && b.partId === partId)?.amount ?? null;

/** Writes one share's budget, replacing any figure already held. */
export function withPartBudget(
  roll: TaxRollRecord,
  year: number,
  partId: string,
  amount: number | null,
): TaxRollRecord {
  const others = (roll.budgets || []).filter(
    (b) => !(b.year === year && b.partId === partId),
  );
  return {
    ...roll,
    budgets: amount === null ? others : [...others, { year, partId, amount }],
  };
}

/** A unit's Budget PA — the sum of the shares it holds. */
export function unitBudgetTotal(
  rolls: TaxRollRecord[],
  unitId: string,
  year: number,
): number | null {
  let total = 0;
  let found = false;
  for (const roll of rolls) {
    for (const part of allocationFor(roll, year).parts) {
      if (part.assignedTo?.type !== "unit" || part.assignedTo.id !== unitId) continue;
      const amount = partBudgetOf(roll, year, part.id);
      if (amount === null) continue;
      total += amount;
      found = true;
    }
  }
  return found ? total : null;
}

/** One roll share a unit holds, with what it cost for a given year. */
export interface RollPartAmounts extends RollPart {
  /** 0–1 of the roll — 1 when the roll isn't split. */
  share: number;
  budget: number | null;
  interim: number | null;
  final: number | null;
  penalty: number;
  total: number;
}

/**
 * A unit's year, roll by roll.
 *
 * The columns show a unit's tax as one figure, which is right for reading down
 * the page but useless for questioning it — C1's is ten parcels added up. This
 * is the same arithmetic left unfolded, so a budget can be argued with.
 */
export function unitRollBreakdown(
  rolls: TaxRollRecord[],
  unitId: string,
  year: number,
  units: Pick<Unit, "id" | "occupancySqFt">[],
): RollPartAmounts[] {
  const out: RollPartAmounts[] = [];

  for (const roll of [...rolls].sort(compareTaxRolls)) {
    const shares = partShares(allocationFor(roll, year), units);
    if (!shares) continue;
    for (const { part, share } of shares) {
      if (part.assignedTo?.type !== "unit" || part.assignedTo.id !== unitId) continue;

      const interimLevy = levyOf(roll, year, "interim");
      const finalLevy = levyOf(roll, year, "final");
      const penalty = [...new Set(
        (roll.levies || []).filter((levy) => levy.year === year).map((levy) => levy.type),
      )].reduce((sum, type) => sum + levyPenalty(roll, year, type), 0) * share;

      const interim = interimLevy ? interimLevy.levy * share : null;
      const final = finalLevy ? finalLevy.levy * share : null;
      out.push({
        roll,
        part,
        share,
        budget: partBudgetOf(roll, year, part.id),
        interim,
        final,
        penalty,
        total: (interim ?? 0) + (final ?? 0) + penalty,
      });
    }
  }

  return out;
}

/**
 * One unit's share of some per-roll figure for a year.
 *
 * null when none of its rolls carry the figure, which is distinct from a real
 * zero and lets the caller fall back to a hand-entered one.
 */
function unitShareTotal(
  rolls: TaxRollRecord[],
  unitId: string,
  year: number,
  units: Pick<Unit, "id" | "occupancySqFt">[],
  valueOf: (roll: TaxRollRecord) => number | null,
): number | null {
  let total = 0;
  let found = false;

  for (const roll of rolls) {
    const value = valueOf(roll);
    if (value === null) continue;
    // The division as it stood THAT year, so a later split can't reach back
    // and change what a settled year cost.
    const shares = partShares(allocationFor(roll, year), units);
    if (!shares) continue;
    for (const { part, share } of shares) {
      if (part.assignedTo?.type !== "unit" || part.assignedTo.id !== unitId) continue;
      total += value * share;
      found = true;
    }
  }

  return found ? total : null;
}

/**
 * Every penalty and credit the town charged in a year, across all rolls.
 *
 * The per-unit shares are already inside each unit's Total PA; this is the
 * portfolio figure, so an admin can see how much of what's due is late-payment
 * cost rather than tax.
 */
export function penaltyTotal(rolls: TaxRollRecord[], year: number): number {
  let total = 0;
  for (const roll of rolls) {
    const types = new Set(
      (roll.levies || []).filter((levy) => levy.year === year).map((levy) => levy.type),
    );
    types.forEach((type) => {
      total += levyPenalty(roll, year, type);
    });
  }
  return total;
}

/**
 * What leaves the bank, by date, across every roll the town bills — including
 * the ones with no unit, because they're paid all the same.
 *
 * This is a CASH figure, not a levy one: it carries the arrears the town has
 * added to an instalment, so it deliberately won't tie back to the year's
 * assessed total.
 */
export function instalmentTotals(
  rolls: TaxRollRecord[],
  year: number,
): { dueDate: string; amount: number }[] {
  const byDate = new Map<string, number>();
  for (const roll of rolls) {
    for (const levy of roll.levies || []) {
      if (levy.year !== year) continue;
      for (const instalment of levy.instalments) {
        byDate.set(
          instalment.dueDate,
          (byDate.get(instalment.dueDate) || 0) + instalment.amount,
        );
      }
    }
  }
  return [...byDate.entries()]
    .map(([dueDate, amount]) => ({ dueDate, amount }))
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

/** Reads a roll the way the bill prints it — what makes a picker usable, since
 *  "000-020-00442-0000" identifies nothing to a human. */
export function taxRollLabel(roll: TaxRollRecord): string {
  const parts = [
    roll.planNumber ? `Plan ${roll.planNumber}` : "",
    roll.level ? `L${roll.level}` : "",
    roll.unit ? `Unit ${roll.unit}` : "",
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : roll.legalDescription || roll.rollNumber;
}

/** Plan → Level → Unit, the order the bill is organised in. Unit compares
 *  numerically so 10 follows 9 rather than 1. */
export function compareTaxRolls(a: TaxRollRecord, b: TaxRollRecord): number {
  const byPlan = (Number(a.planNumber) || 0) - (Number(b.planNumber) || 0);
  if (byPlan) return byPlan;
  const byLevel = a.level.localeCompare(b.level, undefined, { sensitivity: "base" });
  if (byLevel) return byLevel;
  const unitGap = (Number(a.unit) || 0) - (Number(b.unit) || 0);
  if (unitGap) return unitGap;
  return a.rollNumber.localeCompare(b.rollNumber);
}

/** Reads rolls written before rolls could be divided, when a roll carried a
 *  single `assignedTo` and no notion of parts or years. */
export function normalizeTaxRoll(
  roll: TaxRollRecord & { assignedTo?: TaxRollAssignment | null },
): TaxRollRecord {
  const levies = Array.isArray(roll.levies) ? roll.levies : [];
  const budgets = Array.isArray(roll.budgets) ? roll.budgets : [];
  if (Array.isArray(roll.allocations) && roll.allocations.length > 0) {
    return {
      ...roll,
      levies,
      budgets,
      allocations: [...roll.allocations].sort((a, b) => a.fromYear - b.fromYear),
    };
  }
  return {
    ...roll,
    levies,
    budgets,
    // fromYear 0 — it has applied for as long as the roll has existed.
    allocations: [
      {
        id: newId("alloc"),
        fromYear: 0,
        parts: [{ id: newId("part"), label: "", assignedTo: roll.assignedTo ?? null }],
      },
    ],
  };
}

/** How the roll divides today — the allocation the unit form edits. */
export const currentAllocation = (roll: TaxRollRecord): TaxRollAllocation =>
  roll.allocations[roll.allocations.length - 1];

/**
 * How it divided in a given tax year.
 *
 * A settled year keeps its own division — that's the whole point of dating
 * them, so splitting a roll today can't rewrite what a tenant was charged in
 * 2023. But an allocation nobody was ever assigned under isn't a record of how
 * that year divided; it's the absence of one. Releasing a roll and then
 * splitting it would otherwise strand every earlier year's tax with no unit to
 * bear it, while the register read as fully assigned — so in that case the
 * current division stands in.
 */
export const allocationFor = (roll: TaxRollRecord, year: number): TaxRollAllocation => {
  const applicable = roll.allocations.filter((a) => a.fromYear <= year);
  const inForce = applicable.length ? applicable[applicable.length - 1] : roll.allocations[0];
  return inForce.parts.some((part) => part.assignedTo) ? inForce : currentAllocation(roll);
};

/** A part's share reads as a percentage once it has a unit. */
export interface PartShare {
  part: TaxRollPart;
  /** 0–1. */
  share: number;
}

/**
 * How a roll's cost divides between its parts, by the floor area of the units
 * they're assigned to — "if (A) is 60% of the square footage of A+B, they're
 * allocated 60%".
 *
 * null while any part is unassigned: with no unit there's no area, and giving
 * the assigned part everything would overstate one tenant by the other's
 * share. An incomplete roll allocates nothing and blocks the budget instead.
 */
export function partShares(
  allocation: TaxRollAllocation,
  units: Pick<Unit, "id" | "occupancySqFt">[],
): PartShare[] | null {
  if (allocation.parts.some((part) => !part.assignedTo)) return null;
  if (allocation.parts.length === 1) return [{ part: allocation.parts[0], share: 1 }];

  const areaOf = (part: TaxRollPart) =>
    units.find((unit) => unit.id === part.assignedTo?.id)?.occupancySqFt ?? 0;
  const total = allocation.parts.reduce((sum, part) => sum + areaOf(part), 0);

  // Areas not recorded yet — split evenly rather than divide by zero.
  if (total <= 0) {
    return allocation.parts.map((part) => ({ part, share: 1 / allocation.parts.length }));
  }
  return allocation.parts.map((part) => ({ part, share: areaOf(part) / total }));
}

/** Every part with no unit — what the register lists and the budget waits on. */
export const unassignedParts = (
  rolls: TaxRollRecord[],
): { roll: TaxRollRecord; part: TaxRollPart }[] =>
  // Copied before sorting — this runs against the provider's own array.
  [...rolls]
    .sort(compareTaxRolls)
    .flatMap((roll) =>
      currentAllocation(roll)
        .parts.filter((part) => !part.assignedTo)
        .map((part) => ({ roll, part })),
    );

/** A budget can't be built while any tax has nobody to bear it. */
export const allRollsAssigned = (rolls: TaxRollRecord[]): boolean =>
  rolls.length > 0 && unassignedParts(rolls).length === 0;

/** The rolls a unit holds today, in bill order — whole or in part. */
export const rollsForUnit = (rolls: TaxRollRecord[], unitId: string): TaxRollRecord[] =>
  [...rolls]
    .filter((roll) =>
      currentAllocation(roll).parts.some(
        (part) => part.assignedTo?.type === "unit" && part.assignedTo.id === unitId,
      ),
    )
    .sort(compareTaxRolls);

/** One assignable share of a roll, carrying the roll it came off. */
export interface RollPart {
  roll: TaxRollRecord;
  part: TaxRollPart;
}

/**
 * The shares a unit holds today, in bill order.
 *
 * Distinct from `rollsForUnit`: a split roll can have one part here and its
 * sibling somewhere else, so a unit holding "half of 16701" needs the part,
 * not just the roll.
 */
export const rollPartsForUnit = (rolls: TaxRollRecord[], unitId: string): RollPart[] =>
  [...rolls]
    .sort(compareTaxRolls)
    .flatMap((roll) =>
      currentAllocation(roll)
        .parts.filter(
          (part) => part.assignedTo?.type === "unit" && part.assignedTo.id === unitId,
        )
        .map((part) => ({ roll, part })),
    );

/** "000-020-16701-0000 (A)", or just the number while the roll is whole. */
export const rollPartLabel = ({ roll, part }: RollPart): string =>
  part.label ? `${roll.rollNumber} (${part.label})` : roll.rollNumber;

// What a unit was assessed for one tax year. Budget PA is the figure the org
// authors; Interim and Final arrive from the municipality's tax reports. All
// three are entered on the Tax Rolls tab — never on the Unit form, which
// carries only the identity above. Years accumulate rather than being
// replaced, so a unit keeps its full tax history.
export interface TaxYear {
  id: string;
  year: number;
  budgetPa: number | null;
  interim: number | null;
  final: number | null;
}

// One budget year. Its own record, independent of every other year, so a year
// can be worked on without touching the ones around it. The contents arrive in
// a later phase — the year itself is the anchor they hang off.
/**
 * One revenue line, frozen.
 *
 * Deliberately a plain shape rather than revenueBudget.ts's RevenueLine: this
 * file cannot import that one without a cycle, and a stored record should not
 * carry a union type that a later release might narrow — a budget reviewed in
 * 2027 has to still read in 2031.
 */
export interface BudgetRevenueLine {
  key: string;
  label: string;
  qboAccount: string;
  group: string;
  months: number[];
  total: number;
  gap?: string;
  contributions: {
    id: string;
    label: string;
    months: number[];
    total: number;
    /** Months that could not be worked out, as against ones worked out to nil.
     *  Optional: snapshots taken before the distinction existed have none. */
    unknown?: boolean[];
  }[];
}

export interface BudgetRevenueSnapshot {
  /** When the budget was published and the figures fixed. */
  at: string;
  by?: string;
  byName?: string;
  lines: BudgetRevenueLine[];
}

/** One expense line's allocation, frozen: the cost, and who carries it. */
export interface BudgetExpenseSnapshotLine {
  lineId: string;
  description: string;
  code?: string;
  camCategory: string;
  /** The line's own twelve months, as imported or as computed for the fee. */
  months: number[];
  rows: { id: string; months: number[]; total: number }[];
}

/**
 * One tenancy's expense statement, frozen — exactly what the tenant is shown.
 *
 * Built FROM the frozen revenue lines rather than recalculated beside them, so
 * the tenant's figures and the admin's are the same arithmetic rather than two
 * code paths that have to agree. That is what makes "the budget is the single
 * source of truth for tenant expenses" true rather than aspirational.
 */
export interface BudgetStatementSnapshot {
  tenantId: string;
  label: string;
  lines: {
    key: string;
    label: string;
    months: (number | null)[];
    total: number | null;
    emphasis?: boolean;
  }[];
  perSqFt: {
    sqFt: number | null;
    cams: number | null;
    /**
     * One rate per unit, never blended — see the statement's own note.
     *
     * The whole UnitTaxRate rather than just the rate, so a frozen statement
     * renders through exactly the same code as a live one and nothing has to
     * be reconstructed on read.
     */
    taxes: {
      unitId: string;
      label: string;
      sqFt: number | null;
      annual: number;
      psf: number | null;
    }[];
  };
}

/**
 * The whole financial state as it stood when the budget was published.
 *
 * OUTPUTS, not inputs. Freezing the inputs and replaying them would mean a
 * later change to the allocation code could restate a published budget; a
 * frozen output cannot be restated by anything. Measured at 15% of the 2MB
 * document limit on the live portfolio, with room for roughly four times the
 * tenancies before that needs thinking about again.
 */
export interface BudgetPublishedSnapshot {
  at: string;
  by?: string;
  byName?: string;
  /** Effective figures: the projection with the manual adjustments applied. */
  revenue: BudgetRevenueLine[];
  expense: BudgetExpenseSnapshotLine[];
  statements: BudgetStatementSnapshot[];
  /** Tenancy id to the name it had when published, held once rather than per row. */
  tenancies: Record<string, string>;
  /**
   * The year's charges BY UNIT, frozen with everything else — unitId → twelve
   * monthly figures.
   *
   * WHY THE STATEMENTS ARE NOT ENOUGH. A space changes hands during the year,
   * after the budget was published, and the two tenancies then divide what the
   * budget charged for that unit between them. The statements say what each
   * tenancy was charged; only these say what each SPACE was charged, which is
   * what a later handover divides. Without them, a handover could only be
   * settled by republishing the year.
   *
   * Absent on budgets published before this existed; those years divide
   * nothing and read their frozen statements as they always did.
   */
  perUnit?: {
    cam: Record<string, number[]>;
    taxes: Record<string, number[]>;
    dues: Record<string, number[]>;
    royalty: Record<string, number[]>;
    /** unitId → the tenancy the budget charged: the holder at 1 January. */
    charged: Record<string, string>;
  };
}

/** One unlocking of a published budget. Append-only. */
export interface BudgetUnlockEntry {
  at: string;
  by?: string;
  byName?: string;
  /** Mandatory, and enforced server-side as well as in the modal. */
  reason: string;
  /** Stamped by the server. A client-supplied address is a claim, not a fact. */
  ip?: string;
}

export interface BudgetYear {
  id: string;
  year: number;
  /**
   * What the tab is called. Optional: unnamed budgets show their year, which
   * is what every budget starts as. The year stays the budget's identity for
   * ordering and the duplicate check, so renaming a tab is purely a label
   * change and can never collide with another year.
   */
  name?: string;
  /**
   * Progression: which steps have been completed and when. The budget's status
   * is derived from this rather than stored — see budgetHistory.ts.
   */
  history?: BudgetHistory;
  /** The uploaded QBO lines, once categorised. */
  lines: BudgetLine[];
  /** How each category used by those lines is split. */
  splits: CategorySplit[];
  /**
   * The projected revenue as it read when the budget was REVIEWED.
   *
   * Until that step the revenue is projected live, so a lease corrected or a
   * rate restated moves the budget with it — which is what you want while it is
   * being worked up. The moment it is reviewed that stops: the figures are
   * frozen as agreed and the budget is locked, because a signed-off budget that
   * quietly restates itself when next year's BMVA rate is entered is not a
   * record of anything.
   *
   * Absent on a budget still being built, and RETAKEN each time Reviewed is
   * ticked, so what is filed is always the figures that were agreed. A budget
   * whose Reviewed step is un-ticked keeps its snapshot as a record but goes
   * back to reading live, since it is being worked on again.
   *
   * Manual adjustments are NOT folded in
   * — they stay in `revenueOverrides` and are applied over this, so an edited
   * figure still reads as an edit afterwards.
   */
  revenueSnapshot?: BudgetRevenueSnapshot;
  /**
   * The complete frozen state, written when Budget Published is ticked.
   *
   * KEPT WHEN THE BUDGET IS UNLOCKED, as the record of what was posted, but no
   * longer read: an unlocked budget is being worked on again and reads live.
   * Republishing replaces it wholesale, in the same single document write that
   * ticks the step — so a failed or conflicted republish leaves the previous
   * published figures exactly as they were.
   */
  publishedSnapshot?: BudgetPublishedSnapshot;
  /** Every time a published budget was unlocked, and why. Append-only. */
  unlockLog?: BudgetUnlockEntry[];
  // `camRecovery` USED TO LIVE HERE and deliberately does not any more.
  //
  // The 41001 recovery and the 55001 management fee are both derived from this
  // budget's own expense lines, categories and splits — all already on the
  // document — so storing them bought nothing and created figures that could
  // disagree with what they were computed from. They are worked out on read
  // now. Documents written before this still carry a `camRecovery`; it is
  // ignored, and goes on the next write.
  /**
   * Manual adjustments to the projected revenue, keyed "<line key>:<month>"
   * with the month 0-11 — e.g. `"baseRent:6"` for July's Base Rent.
   *
   * The projection states what the leases oblige and is never edited; this is
   * what the admin expects INSTEAD, for a renewal being negotiated or a rent
   * agreed but not yet recorded on the lease. Kept on the budget rather than on
   * the lease so an expectation can never be mistaken for a term — and so two
   * budgets for the same year can carry different assumptions.
   *
   * A key that is absent means "use the projection"; there is deliberately no
   * way to store "edited to exactly the projected figure", because that is not
   * an adjustment.
   */
  revenueOverrides?: Record<string, number>;
  /**
   * Edits to a line's own monthly cost, keyed
   * "<account code>:<month 0-11>".
   *
   * The counterpart of `revenueOverrides`, and the ONLY editable figure on the
   * expense side. A budget line is a forecast, so "Seasons will cost 22,000 in
   * June" gets revised; who carries that cost is not revised here, because it
   * is settled by the category's split under Configuration → CAM Categories.
   * Changing a cost moves every tenancy's share of it, the management fee and
   * the 41001 recovery together.
   *
   * KEYED BY ACCOUNT CODE, not by the line's id, because an import replaces
   * every line and its id with it — an id-keyed edit would be orphaned by the
   * next re-import, invisible and unclearable. A line the export gave no code
   * falls back to its id, having nothing stable to key on.
   *
   * The imported figure is never overwritten: it stays on the line, the cell
   * reads as an edit, and clearing it restores what QuickBooks sent.
   */
  expenseOverrides?: Record<string, number>;
  /**
   * Units whose CAM occupancy this budget STATES rather than derives, keyed
   * unit id to tenancy id. The named tenancy carries that unit's CAM for the
   * whole year, and no Unoccupied remainder appears against it.
   *
   * A budget is prepared before the year begins and assumes twelve even
   * payments from whoever holds a unit — nobody budgets for a tenant leaving in
   * June, because nobody knows they will. When the lease records later show a
   * gap, "who was assumed to be paying for this?" has an answer that no lease
   * date can supply, and this is where it goes.
   *
   * On the BUDGET and not on the lease, deliberately. Blue Mountain Supply
   * Co.'s lease really did end in June 2025 and Freed's really did begin in
   * November; moving those dates to settle a CAM assumption would corrupt
   * rent, percentage rent, BMVA dues, the estoppel certificates and the
   * tenant's own lease summary.
   *
   * CAM ONLY. The tax and BMVA recoveries show the same gaps and are adjusted
   * by hand on the revenue panel instead (user's decision 2026-10-22).
   */
  camOccupancy?: Record<string, string>;
  /**
   * Show this budget's year as a tab on the tenant expense statements.
   *
   * Admins see the tab as soon as it is on, so a year can be read tenant by
   * tenant against the last while it is still being worked up. Tenants see it
   * only once the budget is Reviewed. One budget per year may broadcast — the
   * statement has one tab per year, and two drafts of 2027 would contend for it.
   */
  broadcastExpenses?: boolean;
}

/** A budget's tab label — its name once given one, otherwise its year. */
export const budgetLabel = (budget: Pick<BudgetYear, "year" | "name">): string =>
  budget.name?.trim() || String(budget.year);

// Total PA is what the municipality actually billed: the interim and final
// instalments. Blank instalments count as nothing, so a year that hasn't been
// billed yet totals 0 rather than reading as missing.
export function taxRollTotal(year: Pick<TaxYear, "interim" | "final">): number {
  return (year.interim ?? 0) + (year.final ?? 0);
}

// Next year's budget for a roll: what the municipality billed last year, plus
// the increase. Rounded to cents so the figure reads as money rather than as a
// long float.
export function nextBudgetPa(previousTotalPa: number, percentageIncrease: number): number {
  return Math.round(previousTotalPa * (1 + percentageIncrease / 100) * 100) / 100;
}

// A rentable space within a building.
//
// Everything that can be derived is derived (see bmvaAnnualFee /
// bmvaMonthlyFee below) rather than stored, so a rate correction can never
// leave a stale fee behind. The unit is identified by a stable `id` and points
// at its building by `buildingId` — the same shape the later features need:
// tenant assignments and lease history reference `unit.id`, CAM
// reconciliations aggregate units by `buildingId` against the building's
// camExpense, and sales reporting and documents attach to `unit.id` without
// this interface changing.
export interface Unit {
  id: string;
  buildingId: string;
  /** Suite number. Required, and unique within its building. */
  suiteNumber: string;
  /** One of the configured Space Types. */
  spaceType: string;
  occupancySqFt: number;
  /** The area BMVA charges the basic fee on — genuinely per unit. */
  bmvaSqFt: number;
  /**
   * The rate this unit was last saved with, back when each unit carried its
   * own. BMVA sets one rate for the whole village, so it lives on the
   * Configuration page now and is read through `bmvaRate`.
   *
   * Kept, not dropped: it's the fallback until the configured rate is entered,
   * so no fee reads as zero in the meantime.
   *
   * @deprecated Read `bmvaRate(configuration, unit)` instead.
   */
  bmvaRatePerSqFt: number;
  /**
   * The one roll number that used to be typed on the Unit form, back when a
   * unit was assumed to have exactly one. It doesn't — C1 holds ten — so rolls
   * now live in their own register and are assigned there.
   *
   * Kept, not dropped: it's the seed the first ingest matches against, so a
   * unit whose roll was already keyed comes back pre-assigned. Read-only from
   * here on; nothing writes it.
   */
  taxRoll: TaxRollIdentity;
  /** Assessed amounts by year, newest first. Edited on the Tax Rolls tab. */
  taxYears: TaxYear[];
}

/** The shape rolls had before the identity moved onto the unit. */
interface LegacyTaxRoll {
  rollNumber?: string;
  planNumber?: string;
  level?: string;
  unit?: string;
  budgetPa?: number | null;
  interim?: number | null;
  final?: number | null;
}

/** Sums one amount across a year's rolls; all-blank stays blank, not 0. */
function sumRolls(rolls: LegacyTaxRoll[], key: keyof LegacyTaxRoll): number | null {
  const values = rolls
    .map((roll) => roll[key])
    .filter((value): value is number => typeof value === "number");
  return values.length ? values.reduce((total, value) => total + value, 0) : null;
}

// Reads older units forward. Two earlier shapes exist: a flat `taxRolls` list,
// and `taxYears` whose entries each held their own `rolls[]` carrying both the
// identity and the amounts. The identity is lifted from the newest roll that
// has one; each year keeps that year's amounts, summed if a year somehow held
// more than one roll, so nothing is silently dropped.
export function normalizeUnit(
  unit: Unit & { taxRolls?: LegacyTaxRoll[]; taxYears?: (TaxYear & { rolls?: LegacyTaxRoll[] })[] },
): Unit {
  const years = Array.isArray(unit.taxYears) ? unit.taxYears : [];
  const flat = Array.isArray(unit.taxRolls) ? unit.taxRolls : [];
  const newestFirst = [...years].sort((a, b) => b.year - a.year);

  if (unit.taxRoll && !flat.length && !newestFirst.some((year) => Array.isArray(year.rolls))) {
    return { ...unit, taxYears: newestFirst as TaxYear[] };
  }

  const source =
    newestFirst.flatMap((year) => year.rolls || []).find((roll) => roll.rollNumber) ||
    flat.find((roll) => roll.rollNumber);

  return {
    ...unit,
    taxRoll: unit.taxRoll || {
      rollNumber: source?.rollNumber || "",
      planNumber: source?.planNumber || "",
      level: source?.level || "",
      unit: source?.unit || "",
    },
    taxYears: newestFirst.map((year) =>
      Array.isArray(year.rolls)
        ? {
            id: year.id,
            year: year.year,
            budgetPa: sumRolls(year.rolls, "budgetPa"),
            interim: sumRolls(year.rolls, "interim"),
            final: sumRolls(year.rolls, "final"),
          }
        : (year as TaxYear),
    ),
  };
}

type ExemptConfig = Pick<
  TospConfiguration,
  "bmvaExemptSpaceTypes" | "bmvaExemptUnitIds"
>;
type RateConfig = ExemptConfig & Pick<TospConfiguration, "bmvaRatePerSqFt">;
type UnitFee = Pick<Unit, "id" | "bmvaSqFt" | "bmvaRatePerSqFt" | "spaceType">;

/**
 * Whether BMVA charges the basic fee on this unit at all.
 *
 * Two ways to be exempt, because they're two different statements: the Space
 * Type rule is about a kind of space and holds for units that don't exist yet,
 * while naming a unit covers the one-off arrangements a rule can't express.
 */
export const isBmvaExempt = (
  configuration: ExemptConfig,
  unit: Pick<Unit, "id" | "spaceType">,
): boolean =>
  (configuration.bmvaExemptUnitIds || []).includes(unit.id) ||
  (configuration.bmvaExemptSpaceTypes || []).some(
    (exempt) => exempt.trim().toLowerCase() === (unit.spaceType || "").trim().toLowerCase(),
  );

/**
 * Whether percentage rent is charged on this unit at all.
 *
 * The lease states one Breakpoint Rate for the whole agreement, and it reaches
 * the commercial premises rather than the basement or the storage locker. So
 * the rate is a lease term and this is the filter on which of the lease's
 * spaces it actually applies to.
 *
 * A unit whose Space Type isn't on the list contributes no breakpoint, which
 * is not the same as a breakpoint of zero — see naturalBreakpointYear.
 */
export const chargesPercentageRent = (
  configuration: Pick<TospConfiguration, "percentageRentSpaceTypes">,
  unit: Pick<Unit, "spaceType"> | undefined,
): boolean =>
  !!unit &&
  (configuration.percentageRentSpaceTypes || []).some(
    (type) => type.trim().toLowerCase() === (unit.spaceType || "").trim().toLowerCase(),
  );

/**
 * The rate a unit's basic fee is charged at.
 *
 * Configuration is the answer; the unit's own stored rate is only a fallback,
 * for the window before an admin has entered BMVA Fees on that screen. Once the
 * configured rate is set it wins everywhere, so raising it from $1.00 to $1.08
 * is one edit rather than sixty and no unit can be missed.
 *
 * Zero for an exempt Space Type — Storage pays no basic fee — and that beats
 * the stored fallback too, so an exemption can't be undone by a rate a unit
 * was saved with years ago.
 */
export const bmvaRate = (configuration: RateConfig, unit?: UnitFee): number => {
  if (unit && isBmvaExempt(configuration, unit)) return 0;
  return configuration.bmvaRatePerSqFt ?? unit?.bmvaRatePerSqFt ?? 0;
};

export const bmvaAnnualFee = (unit: UnitFee, configuration: RateConfig): number =>
  unit.bmvaSqFt * bmvaRate(configuration, unit);

export const bmvaMonthlyFee = (unit: UnitFee, configuration: RateConfig): number =>
  bmvaAnnualFee(unit, configuration) / 12;

/**
 * The royalty a tenant pays on sales — BMVA's, not the lease's.
 *
 * Every lease states the same figure and it changes for everybody at once, so
 * it's read from Configuration. The per-occupancy value is kept only as the
 * fallback for leases reviewed before this moved.
 */
export const bmvaRoyalty = (
  configuration: Pick<TospConfiguration, "bmvaRoyaltyPercentage">,
  occupancy?: Pick<Occupancy, "bmvaPercentage">,
): number | null =>
  configuration.bmvaRoyaltyPercentage ?? occupancy?.bmvaPercentage ?? null;

export const RENT_STEP_UP_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export type RentStepUpMonth = (typeof RENT_STEP_UP_MONTHS)[number];

// A renewal: the lease starts again at a negotiated rate on a given date. It
// is a rent commencement in its own right, not an adjustment to the previous
// one, which is why the earlier figures are unaffected by it.
export interface RentReset {
  id: string;
  /** ISO yyyy-mm-dd the new rent takes effect. */
  rentCommenceDate: string;
  /** $ per square foot from that date. */
  rentPsfCommencement: number | null;
  /**
   * Set when the renewal was read off an amendment rather than typed in.
   *
   * It carries no calculation — the figures are the same either way. What it
   * changes is who may remove it: a renewal that came from a signed document
   * is a fact about the agreement, and the form has no business offering to
   * delete it. Renewals added by hand keep their ×.
   *
   * Absent on every renewal recorded before amendments were read, which is
   * correct: those were all typed.
   */
  fromAmendment?: boolean;
}

// One rentable area governed by a lease.
//
// A lease demises several: MJ Byrne's covers a ground floor, a basement, a
// terrace and a storage locker, each with its own rent. They are not separate
// leases — the term, the extensions, the use clause and the deposit are the
// agreement's, not any one space's — so they sit inside the Occupancy rather
// than beside it.
//
// Nothing about the unit is copied in. Space Type and Occupancy SqFt stay on
// the Unit record and are read through `unitId`, so a corrected unit can't
// leave stale figures on a lease, and the unit list — not the lease — is what
// the portfolio's areas are measured from.
export interface LeasedSpace {
  id: string;
  /** What the lease calls it — "Ground Floor", "Terrace Agreement". */
  name: string;
  /**
   * The TOSP unit this space is charged through. Blank where the lease grants
   * a space that generates neither rent nor taxes — a terrace, usually — which
   * is a recorded fact about the agreement rather than an unfinished record.
   */
  unitId: string;

  /** ISO yyyy-mm-dd rent first becomes payable on this space. */
  rentCommenceDate: string;
  /** $ per square foot at that commencement. */
  rentPsfCommencement: number | null;
  /**
   * Renewals that restart this space's rent calculation. Each is a fresh
   * commencement effective from its own date and applying only forward; the
   * original above is never overwritten, so history stays intact.
   */
  rentResets: RentReset[];
  /**
   * Whether this space's rent escalates at all.
   *
   * Off is a real answer, not an unfilled one: a storage locker at a flat
   * $16.50 doesn't step up, and a blank increase would otherwise be
   * indistinguishable from one nobody has recorded yet. Off means the
   * commencement rate stands for the whole term.
   */
  annualIncreaseEnabled: boolean;
  /** Percentage. Only read when annualIncreaseEnabled. */
  minimumIncrease: number | null;
  /** Percentage. Only read when annualIncreaseEnabled. */
  maximumIncrease: number | null;
  /** Only read when annualIncreaseEnabled. */
  rentStepUpMonth: RentStepUpMonth | "";

  /**
   * $ cap on the BMVA charge for this space — the exception to the royalty.
   * A space that names a cap pays that instead of the royalty on sales.
   */
  bmvaCap: number | null;
}

// One lease: the agreement between a tenant and the landlord, and everything
// it governs. A tenant may hold several at once and several over time, and
// ended leases are kept rather than removed, so a unit's history is simply
// every lease that has ever demised it.
//
// The name is historical — the record models the legal lease, not one physical
// space. What varies space by space lives in `leasedSpaces`; everything here
// is stated once for the whole agreement. Dates are ISO yyyy-mm-dd and money /
// rate fields are null when not recorded, which is distinct from a real 0.
export interface Occupancy {
  id: string;

  /**
   * The areas this lease demises, each with its own rent. Always at least one
   * on a record an admin has finished; a lease read forward from the shape
   * that predated this carries exactly one, built from its own fields.
   */
  leasedSpaces: LeasedSpace[];

  occupancyDate: string;
  openingDate: string;
  leaseCommenceDate: string;
  /**
   * The date the lease was signed.
   *
   * Distinct from every other date here: those are when the tenancy does
   * something — possession, opening, rent starting — and this is when the
   * document was executed, which can be months earlier and governs nothing.
   *
   * Optional because no lease saved before it existed carries one. Blank reads
   * as "not recorded", not as a date of nothing.
   */
  leaseExecutionDate?: string;
  /** Initial term as entered, e.g. "120 months". */
  leaseTerm: string;
  /**
   * An explicit end, set when the lease is cut short — a takeover ends the
   * outgoing tenant here. Blank means the term runs its natural course and the
   * end is derived from the commence date and the extensions.
   */
  leaseEndDate: string;
  /** Whether the lease carries extension terms. */
  extensionTermEnabled: boolean;
  /** Each extension term as entered, e.g. "60 months". */
  extensionTerms: string[];
  /**
   * How many of the extension terms have been exercised, counted from the
   * first. An amendment (LAA) that renews into an extension marks one used;
   * the exercised terms are greyed on the form rather than removed, so it
   * stays visible that the tenant HELD extensions and has now taken one up.
   * Purely a marker — it does not change the derived dates, which still count
   * every extension the lease grants. Absent on records that predate this.
   */
  extensionTermsUsed?: number;
  // Next Renewal Date and Date Under Contract are derived from the commence
  // date, the term and the extensions — see the schedule functions below.

  /**
   * Percentage — the rate the breakpoints are worked out from.
   *
   * Stated once for the agreement, but charged only on the spaces whose Space
   * Type attracts percentage rent: the commercial premises, not the basement
   * or the storage. See chargesPercentageRent.
   */
  percentageRentThresholdRate: number | null;
  /** Percentage — the rate actually paid above the breakpoint. */
  percentageRentPayment: number | null;
  // Each qualifying space's breakpoint is derived from its unit's area, its own
  // rent rate and the rate above — see percentageRentThreshold below.

  /**
   * Whether this tenant reports their monthly sales at all.
   *
   * Some don't and never will — a government retailer or a national chain
   * whose lease carries no percentage rent files nothing, and chasing them
   * every month buries the tenants who genuinely are behind.
   *
   * Absent means yes. Reporting is the norm and the useful default, and it
   * keeps every record written before this existed behaving as it did.
   */
  salesReported?: boolean;

  /**
   * $ security deposit the lease requires.
   *
   * A term of the agreement rather than a fact about the tenant — one company
   * holding three leases owes three deposits — so it's held here and verified
   * against the document like any other term.
   */
  securityDepositAmount: number | null;
  /**
   * ISO yyyy-mm-dd the deposit was actually paid.
   *
   * Kept beside the amount but deliberately not reviewed: the lease says what
   * is owed, never when it arrived, so there is nothing in the document to
   * check it against.
   */
  depositPaidDate: string;

  /**
   * The royalty this occupancy was reviewed at. BMVA charges every tenant the
   * same rate and changes it for all of them at once, so it's set on the
   * Configuration page now and read through `bmvaRoyalty`.
   *
   * Kept as the fallback for occupancies entered before the move.
   *
   * @deprecated Read `bmvaRoyalty(configuration, occupancy)` instead.
   */
  bmvaPercentage: number | null;
  /**
   * ANNUAL $ cap on the BMVA charge — the exception, and still per tenant. A
   * lease that names a cap pays that instead of the royalty on sales, and the
   * budget spreads it over twelve like any other royalty.
   *
   * This is what lets a tenant who reports NO sales still carry a royalty: LCBO
   * pays a set sum the landlord remits to BMVA and recovers inside their merged
   * CAM charge.
   *
   * bmvaRoyaltyLine takes the FIRST cap it finds on the tenancy, looking at this
   * field and at the leased spaces' own — the only box in the UI is on the space
   * card, while this one is set by lease extraction alone. Never the sum: the cap
   * is per tenancy, so a tenant with a shop and a locker owes it once.
   */
  bmvaCap: number | null;

  /**
   * The party named as Landlord on the lease — which Bydem entity (or a
   * predecessor) the agreement is with. Per lease, not per portfolio: the
   * leases are held by several entities, and an amendment or a transfer can
   * move one lease to another without touching the rest.
   */
  landlordLegalEntity: string;
  notes: string;
  rentRenewalNotations: string;
  /**
   * Free text for what an amendment (LAA) changed beyond the extracted figures
   * — a landlord change, an acknowledgement of no further extensions, a
   * concurrent side agreement. Kept apart from rentRenewalNotations (the
   * original lease's renewal options) and shown in the amendment blue.
   */
  extensionNotes: string;
  useClauseSummary: string;
  exclusiveSummary: string;

  /**
   * The SMRT Docs document key holding this occupancy's signed lease, set the
   * first time an admin picks it. A tenant contact can hold dozens of
   * documents across several services, and nothing in the record links one to
   * a unit — so the admin's choice is remembered rather than re-asked.
   */
  leaseDocumentKey: string;
  /** The last completed review, or null if this occupancy has never had one. */
  leaseReview: LeaseReviewStamp | null;
  /**
   * Field keys an amendment (LAA) changed, so the form paints them in the
   * amendment blue. Durable: it records which terms on the current record came
   * from an amendment rather than the original lease. Space-scoped keys are
   * namespaced as `<spaceId>:<key>`, the same convention the review uses.
   */
  amendedFields?: string[];

  // ── The shape that predated Leased Spaces ──────────────────────────────────
  //
  // A record used to be one lease of one unit, so the unit and its rent sat
  // directly here. Kept rather than dropped: normalizeOccupancy reads them
  // forward into a single leased space, and leaving the originals in place
  // means a record that has been read forward can still be checked against
  // what was actually saved.
  //
  // Nothing writes these, and nothing but normalizeOccupancy reads them.

  /** @deprecated read through `leasedSpaces[].unitId`. */
  unitId?: string;
  /** @deprecated read through `leasedSpaces[].rentCommenceDate`. */
  rentCommenceDate?: string;
  /** @deprecated read through `leasedSpaces[].rentPsfCommencement`. */
  rentPsfCommencement?: number | null;
  /** @deprecated read through `leasedSpaces[].rentResets`. */
  rentResets?: RentReset[];
  /** @deprecated read through `leasedSpaces[].minimumIncrease`. */
  minimumIncrease?: number | null;
  /** @deprecated read through `leasedSpaces[].maximumIncrease`. */
  maximumIncrease?: number | null;
  /** @deprecated read through `leasedSpaces[].rentStepUpMonth`. */
  rentStepUpMonth?: RentStepUpMonth | "";
}

/** A leased space with nothing filled in, ready to be named and assigned. */
export function emptyLeasedSpace(name = ""): LeasedSpace {
  return {
    id: newId("spc"),
    name,
    unitId: "",
    rentCommenceDate: "",
    rentPsfCommencement: null,
    rentResets: [],
    annualIncreaseEnabled: true,
    minimumIncrease: null,
    maximumIncrease: null,
    rentStepUpMonth: "",
    bmvaCap: null,
  };
}

/**
 * Reads a lease saved before Leased Spaces existed into the shape everything
 * now expects.
 *
 * One space, carrying the unit and rent the record already held. Read forward
 * at load rather than migrated in place: nothing has to be rewritten, a record
 * that was never opened again still reads correctly, and the original fields
 * stay on the document as written.
 *
 * The space is left unnamed. Naming it would mean inventing a description the
 * lease never gave it, and the reviewer names it the next time the lease is
 * reviewed; until then it shows as the unit it points at.
 */
export function normalizeOccupancy(occupancy: Occupancy): Occupancy {
  if (Array.isArray(occupancy.leasedSpaces)) return occupancy;

  return {
    ...occupancy,
    leasedSpaces: [
      {
        ...emptyLeasedSpace(),
        // Deterministic, so reading the same record twice gives the same space
        // rather than a new id each load — which would reset every React key
        // and lose the reviewer's place on every save.
        id: `${occupancy.id}_spc`,
        unitId: occupancy.unitId || "",
        rentCommenceDate: occupancy.rentCommenceDate || "",
        rentPsfCommencement: occupancy.rentPsfCommencement ?? null,
        rentResets: occupancy.rentResets ?? [],
        // On where the old record actually carried an escalation, so nothing
        // read forward loses one; off where it never had any.
        annualIncreaseEnabled:
          occupancy.minimumIncrease !== null ||
          occupancy.maximumIncrease !== null ||
          !!occupancy.rentStepUpMonth,
        minimumIncrease: occupancy.minimumIncrease ?? null,
        maximumIncrease: occupancy.maximumIncrease ?? null,
        rentStepUpMonth: occupancy.rentStepUpMonth ?? "",
        bmvaCap: occupancy.bmvaCap ?? null,
      },
    ],
    securityDepositAmount: occupancy.securityDepositAmount ?? null,
    depositPaidDate: occupancy.depositPaidDate ?? "",
  };
}

/** Every space of every lease a tenant holds, each with the lease it belongs to. */
export const tenantLeasedSpaces = (
  tenant: Pick<Tenant, "occupancies">,
): { occupancy: Occupancy; space: LeasedSpace }[] =>
  (tenant.occupancies || []).flatMap((occupancy) =>
    (occupancy.leasedSpaces || []).map((space) => ({ occupancy, space })),
  );

/**
 * A space's rent escalation: its own rent terms, and the lease's commencement
 * date.
 *
 * The date is the lease's because a space has no commencement of its own — it
 * is demised by the agreement, and the agreement begins once. This is what
 * lets every rent function below stay exactly as it was: they were always
 * written against RentEscalation rather than against a record.
 */
export const spaceEscalation = (
  occupancy: Pick<Occupancy, "leaseCommenceDate">,
  space: LeasedSpace,
): RentEscalation => ({
  rentCommenceDate: space.rentCommenceDate,
  leaseCommenceDate: occupancy.leaseCommenceDate,
  rentPsfCommencement: space.rentPsfCommencement,
  rentResets: space.rentResets || [],
  // Switched off is a rate of ZERO, not a missing one. Null means "nobody has
  // said yet" and leaves every rate unknowable; zero means the commencement
  // rate stands, which is what a flat rent actually is.
  minimumIncrease: space.annualIncreaseEnabled ? space.minimumIncrease : 0,
  maximumIncrease: space.annualIncreaseEnabled ? space.maximumIncrease : 0,
  rentStepUpMonth: space.annualIncreaseEnabled ? space.rentStepUpMonth : "",
});

/**
 * What a completed lease review leaves behind.
 *
 * Verification is work, and work already done shouldn't have to be redone to
 * change one figure. The stamp records which document was checked and which
 * fields were signed off against it, so reopening the same lease starts from
 * where the last review finished.
 *
 * It's tied to the document rather than to the occupancy on purpose. A
 * replacement lease uploaded over the old one is a different agreement, and
 * carrying the old sign-off forward onto it would mean the record claims terms
 * were verified against a document nobody has read.
 */
export interface LeaseReviewStamp {
  /** Fingerprint of the document reviewed — changes when the file does. */
  documentHash: string;
  /** ISO timestamp of the Save that completed the review. */
  reviewedAt: string;
  /**
   * Who signed it off. Optional: reviews recorded before this was captured
   * have no name, and the banner simply omits it rather than inventing one.
   */
  reviewedBy?: string;
  /** Field keys signed off. Keys added to the review later simply aren't here. */
  verified: string[];
  /**
   * True when what was signed off was an amendment (LAA) rather than the lease.
   *
   * Without it, reopening an amendment that had already been applied looked
   * identical to reopening an unchanged lease — same document, same hash — so
   * the amendment panel never came back and its confirmed rows couldn't be
   * seen or corrected. A lease can always be re-reviewed; an amendment has to
   * be able to as well.
   *
   * Absent on reviews recorded before this, which read as lease reviews. That
   * is the safe way round: an amendment mistaken for a lease asks for the
   * terms again, where a lease mistaken for an amendment would carry unchecked
   * terms forward as verified.
   */
  amendment?: boolean;
}

/** Somebody the tenant has asked to act for them. */
export interface TenancyDelegate {
  /** The Auth0 account. Authoritative — an email can be re-pointed. */
  userId: string;
  email: string;
  name: string;
  /** What they may do. "report" covers the dashboard and submitting a month. */
  capability: "report";
  invitedBy: string;
  invitedAt: string;
}

/** One handover of a tenancy from one person to another. */
export interface TenancyTransfer {
  /** ISO timestamp. */
  at: string;
  /** Who performed it — not who received it. */
  byEmail: string;
  byName: string;
  fromEmail: string;
  toEmail: string;
  toName: string;
  /** Whether the SMRT Docs service was reassigned. */
  serviceMoved: boolean;
  /** How many documents followed. Zero when they were left behind. */
  documentsMoved: number;
}

export interface Tenant {
  id: string;
  /**
   * What the tenant trades as — the name on the unit, in every list, and on
   * their own portal. The lease governs it: "Tenant may operate its business
   * at the Premises only under the name …".
   */
  operatingName: string;
  /**
   * The party that signs the lease and is pursued for the rent. Usually the
   * operating name with a corporate suffix, but not always — one company can
   * trade under several names, and a name can outlive the company behind it,
   * which is why these are two fields rather than one.
   */
  legalEntity: string;
  /** One of the configured Retailer Types. */
  retailerType: string;
  primaryContactName: string;
  primaryContactPhone: string;
  primaryContactEmail: string;
  /** One of the configured Waste Removal Types. */
  wasteRemoval: string;
  atmRestriction: boolean;
  atmOnsite: boolean;
  /** Optional. Null when no deposit has been recorded — distinct from a real 0. */
  depositPaidAmount: number | null;
  /** Optional, ISO yyyy-mm-dd. */
  depositPaidDate: string;
  /** Rent paid up front. Optional, and absent on tenants saved before it existed. */
  prepaidRentAmount?: number | null;
  /** Optional, ISO yyyy-mm-dd. */
  prepaidRentDate?: string;
  /**
   * The SMRT Docs service holding this tenancy's lease.
   *
   * One contact can hold several tenancies — a person who runs two businesses
   * in the building — and each has its own lease on its own service. Without
   * this, "which of their leases is this tenant's?" has no answer: the contact
   * is the same, and the service names are only a convention. Recorded the
   * first time a lease is reviewed, so the question is asked once and never
   * again.
   *
   * Empty on tenants created before this was stored; the reviewer is asked to
   * choose, and the answer is kept.
   */
  smrtDocsServiceId: string;
  /**
   * Who held this tenancy before the last transfer, and when.
   *
   * A transfer changes who can see a business.s financials and open its
   * lease, and used to leave no trace at all: once the contact was replaced,
   * "whose record are the papers on?" had no stored answer. Kept so a
   * half-finished handover can be traced rather than remembered.
   */
  previousContactEmail: string;
  /** ISO timestamp of the last transfer, empty if never transferred. */
  contactTransferredAt: string;
  /**
   * Every transfer this tenancy has been through, oldest first.
   *
   * Kept on the tenancy rather than in a container of its own: a transfer is
   * an event in one tenancy.s life, it is rare, and holding it here means the
   * audit view is a read of records the module already has open.
   */
  transferLog: TenancyTransfer[];
  /**
   * People the tenant has asked to act for them — a manager or an accountant
   * who reports the monthly sales.
   *
   * Held per tenancy, not per person: a contact with three businesses can
   * delegate one of them. Revoking is removing a row.
   */
  delegates: TenancyDelegate[];
  occupancies: Occupancy[];
}

/**
 * How a tenant is referred to wherever one name is wanted — a grid row, a
 * takeover warning, the portal's greeting.
 *
 * The operating name wins because that's who the tenant is to everyone who
 * deals with them; the legal entity stands in only where there's no operating
 * name to use, so a record can never render as a blank.
 */
export const tenantLabel = (
  tenant: Pick<Tenant, "operatingName" | "legalEntity">,
): string => tenant.operatingName?.trim() || tenant.legalEntity?.trim() || "";

/**
 * Tenants once carried a single `name`, which in practice held whichever of
 * the two the admin had to hand.
 *
 * It's read as both, rather than guessed at: a record that showed the right
 * name in lists but had a blank legal entity would look complete while missing
 * the party the lease is actually with. Two identical values are visibly
 * unsplit, and the admin separates them the next time they open the record —
 * or the lease review does it for them.
 */
export function normalizeTenant(tenant: Tenant & { name?: string }): Tenant {
  const { name, ...rest } = tenant;
  const legacy = (name || "").trim();
  return {
    ...rest,
    operatingName: (tenant.operatingName || "").trim() || legacy,
    legalEntity: (tenant.legalEntity || "").trim() || legacy,
    // Absent on anything saved before the lease was tied to a service. Read
    // forward as empty, which is what makes the reviewer be asked once.
    smrtDocsServiceId: (tenant.smrtDocsServiceId || "").trim(),
    previousContactEmail: (tenant.previousContactEmail || "").trim(),
    contactTransferredAt: (tenant.contactTransferredAt || "").trim(),
    transferLog: Array.isArray(tenant.transferLog) ? tenant.transferLog : [],
    delegates: Array.isArray(tenant.delegates) ? tenant.delegates : [],
  };
}

/** Today as yyyy-mm-dd, so ISO date strings can be compared lexically. */
export const todayISO = (): string => new Date().toISOString().slice(0, 10);

// The lease schedule: everything the renewal and contract-end dates are worked
// out from. Drafts on the Tenant form satisfy this shape too, so the card can
// show live figures before anything is saved.
export interface LeaseSchedule {
  leaseCommenceDate: string;
  leaseTerm: string;
  /** An explicit end, when the lease was cut short. */
  leaseEndDate: string;
  extensionTermEnabled: boolean;
  extensionTerms: string[];
  /**
   * How many extensions have been taken up, counted from the first.
   *
   * Needed here because an exercised extension moves the next renewal: the
   * milestone it renewed at is settled, not still ahead.
   */
  extensionTermsUsed?: number;
}

/** The day before an ISO date — where a handover ends the outgoing lease. */
export function dayBefore(iso: string): string {
  const [year, month, day] = (iso || "").split("-").map(Number);
  if (!year || !month || !day) return "";
  const date = new Date(year, month - 1, day - 1);
  const pad2 = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

// Terms are entered as free text ("60 months", "60"), so read the leading
// figure and ignore the rest.
export function monthsFromTerm(term: string): number | null {
  const match = /-?\d+(\.\d+)?/.exec(term || "");
  if (!match) return null;
  const months = Math.round(Number(match[0]));
  return Number.isFinite(months) && months > 0 ? months : null;
}

const pad = (n: number) => String(n).padStart(2, "0");

// A term of N months starting on the 1st runs to the day before the same date
// N months later: Jan 1 2015 + 120 months → Dec 31 2024.
function addMonthsMinusDay(startISO: string, months: number): string | null {
  const [year, month, day] = (startISO || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  const end = new Date(year, month - 1 + months, day);
  end.setDate(end.getDate() - 1);
  return `${end.getFullYear()}-${pad(end.getMonth() + 1)}-${pad(end.getDate())}`;
}

// Each point at which the lease either renews into an extension or finally
// ends — the initial term, then each extension in turn, as running totals from
// the commence date.
export function leaseMilestones(schedule: LeaseSchedule): string[] {
  const initial = monthsFromTerm(schedule.leaseTerm);
  if (!schedule.leaseCommenceDate || initial === null) return [];
  const extensions = schedule.extensionTermEnabled
    ? schedule.extensionTerms.map(monthsFromTerm).filter((m): m is number => m !== null)
    : [];
  let cumulative = 0;
  return [initial, ...extensions]
    .map((months) => {
      cumulative += months;
      return addMonthsMinusDay(schedule.leaseCommenceDate, cumulative);
    })
    .filter((date): date is string => date !== null);
}

/** The end of the last extension — how long the unit is committed for. */
export function dateUnderContract(schedule: LeaseSchedule): string | null {
  const milestones = leaseMilestones(schedule);
  return milestones.length ? milestones[milestones.length - 1] : null;
}

// The next milestone still ahead: the end of whichever term is running now.
// Once every term has passed there is nothing left to renew into.
//
// Milestones already settled by an exercised extension are skipped. A lease
// commencing 2001-11-01 for 25 years reaches a milestone on 2026-10-31, but an
// amendment taking up the five-year extension has already answered what happens
// there — the lease now runs to 2031-10-31, and that is the next date anyone
// needs to act on. Milestone i is the decision to take extension i+1, so N
// extensions used means the first N milestones have been decided.
export function nextRenewalDate(schedule: LeaseSchedule): string | null {
  const today = todayISO();
  const decided = Math.max(0, schedule.extensionTermsUsed ?? 0);
  return (
    leaseMilestones(schedule)
      .slice(decided)
      .find((date) => date >= today) || null
  );
}

/** Whole days from one ISO date to another. Negative when `to` is past. */
function daysBetween(fromISO: string, toISO: string): number | null {
  // Parsed as UTC midnight on both sides: a local-time subtraction across a
  // daylight-saving boundary lands on 179.96 days and rounds unpredictably,
  // which on a countdown shows as a day that never ticks or ticks twice.
  const from = Date.parse(`${fromISO}T00:00:00Z`);
  const to = Date.parse(`${toISO}T00:00:00Z`);
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((to - from) / 86_400_000);
}

/**
 * A span between two dates as whole years plus the days left over.
 *
 * Counted by anniversary rather than by dividing the day count, so it is exact
 * whatever leap years fall inside it — 365-day arithmetic drifts a day every
 * four years, and a lease running to 2031 would be a day out for the last four
 * of them. Order doesn't matter: a span measured backwards from an expiry is
 * the same span.
 */
export interface DateSpan {
  years: number;
  days: number;
}

export function spanBetween(fromISO: string, toISO: string): DateSpan | null {
  const [early, late] = fromISO <= toISO ? [fromISO, toISO] : [toISO, fromISO];
  const [year, month, day] = early.split("-").map(Number);
  if (!year || !month || !day) return null;

  // The anniversary of `early` in a given year. A 29 February start rolls to
  // 1 March in a common year, which is the convention a date input already
  // uses and the only one that keeps the count monotonic.
  const anniversary = (after: number): string => {
    const date = new Date(year + after, month - 1, day);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  let years = 0;
  while (anniversary(years + 1) <= late) years += 1;

  const days = daysBetween(anniversary(years), late);
  return days === null ? null : { years, days };
}

/**
 * A span in words: "5 years 64 days", "64 days", "5 years".
 *
 * The years are what the reader is actually after — "1,889 days" has to be
 * divided in the head before it means anything — but the days stay on, because
 * once a lease is inside its final year the days are the whole story.
 */
export function formatSpan(span: DateSpan): string {
  const years = span.years === 1 ? "1 year" : `${span.years} years`;
  const days = span.days === 1 ? "1 day" : `${span.days} days`;
  if (span.years === 0) return days;
  if (span.days === 0) return years;
  return `${years} ${days}`;
}

/** Notice a tenant must give before a term ends, when the lease is silent. */
export const DEFAULT_RENEWAL_NOTICE_DAYS = 180;

/**
 * How many days' notice the lease requires, read from the renewal notations.
 *
 * The period is extracted as prose rather than as a number — "Notice 180 days
 * before expiry of the term" — so it is read back out here. Anchored on
 * before/prior so a figure that merely sits near the word "days" (a cure
 * period, a holdover) can't be mistaken for the notice period.
 */
export function renewalNoticeDays(notations: string): number {
  const match = /(\d{1,4})\s*days?\s+(?:before|prior)\b/i.exec(notations || "");
  const days = match ? Number(match[1]) : NaN;
  return Number.isFinite(days) && days > 0 ? days : DEFAULT_RENEWAL_NOTICE_DAYS;
}

/**
 * Where a lease stands against its own end date.
 *
 * Two stages, deliberately: counting DOWN once inside the notice period, then
 * counting UP once the date has passed. The change of direction is the point —
 * "expires in 12 days" is a task, "expired 40 days ago" is an exposure, and a
 * warning that stops at zero would quietly disappear at exactly the moment it
 * started to matter.
 */
export type LeaseExpiry =
  /** Nothing to work from — no commence date, or no term. */
  | { state: "unknown" }
  /** More than a year still to run — nothing to plan for yet. */
  | { state: "ok"; on: string; days: number; noticeDays: number }
  /**
   * Under a year left, with more than 180 days still to run.
   *
   * Its own state because it answers a different question from the countdown:
   * not "is there something to do this month" but "is this lease inside the
   * horizon being planned". An expiry eleven months out is a budget line, not
   * yet a deadline — flagged, but blue rather than amber.
   */
  | { state: "withinYear"; on: string; days: number; noticeDays: number }
  /** Under a year left and now 180 days or fewer away — the run-in to renew or
   *  give notice. */
  | { state: "expiring"; on: string; days: number; noticeDays: number }
  | { state: "expired"; on: string; days: number };

export function leaseExpiryFor(
  schedule: LeaseSchedule,
  notations: string,
): LeaseExpiry {
  const milestones = leaseMilestones(schedule);

  // An explicit end wins — a lease cut short by a takeover ends when it says,
  // not when its term would have run out. Otherwise it is the end of the term
  // running now; and once every term has passed, the last one there was.
  const end =
    schedule.leaseEndDate ||
    nextRenewalDate(schedule) ||
    milestones[milestones.length - 1] ||
    "";
  if (!end) return { state: "unknown" };

  const days = daysBetween(todayISO(), end);
  if (days === null) return { state: "unknown" };
  if (days < 0) return { state: "expired", on: end, days: -days };

  const noticeDays = renewalNoticeDays(notations);

  // Green until the lease has under a year to run. A rolling twelve months, not
  // the calendar year: a lease ending next January matters just as much in the
  // December before it, where a calendar test would have shown it green until
  // the day it fell inside the notice period. Counted by anniversary rather
  // than as 365 days, so a leap year cannot move the boundary.
  const left = spanBetween(todayISO(), end);
  if (!left || left.years >= 1) return { state: "ok", on: end, days, noticeDays };

  // Under a year left: blue while there is still runway, amber once inside
  // the 180-day run-in to renew or give notice. A fixed window, not the lease's
  // own notice period — the same horizon is wanted across every tenant.
  const RUN_IN_DAYS = 180;
  if (days <= RUN_IN_DAYS) return { state: "expiring", on: end, days, noticeDays };
  return { state: "withinYear", on: end, days, noticeDays };
}

/**
 * The most pressing expiry across everything a tenant holds.
 *
 * A tenant with several occupancies gets one warning, for whichever is nearest
 * — an expired lease outranks an expiring one, and among expiring ones the
 * soonest wins. Showing all of them would bury the one that needs doing.
 */
/**
 * The day a tenant began trading, across everything they hold.
 *
 * The earliest of their leases, because a tenant who took a second unit later
 * has been trading since the first one opened. Used to stop the sales chaser
 * demanding figures from before a tenant existed: a shop opening next month
 * owes nothing for this one, and a brand new record with no reported months
 * would otherwise be chased for a full year of them.
 *
 * The opening date alone. Rent commencement is the tempting stand-in and the
 * wrong one: it is a term of the lease and rarely moves, while the day a shop
 * actually opens slips — so a tenant whose opening ran late would be chased
 * for months they were still fitting out.
 */
/**
 * Whether this tenant reports monthly sales at all.
 *
 * Set on the lease, read for the tenant: they are exempt only when EVERY
 * lease they hold says so. One lease that still reports is enough to keep
 * them reporting, since the figures are filed per tenant, not per lease.
 *
 * A tenant with no leases reports — nothing has said otherwise.
 */
export function tenantReportsSales(tenant: { occupancies?: Occupancy[] }): boolean {
  const leases = tenant.occupancies || [];
  if (leases.length === 0) return true;
  return leases.some((occupancy) => occupancy.salesReported !== false);
}

export function tenantOpeningDate(tenant: { occupancies?: Occupancy[] }): string | null {
  const dates = (tenant.occupancies || [])
    .map((occupancy) => occupancy.openingDate || "")
    .filter(Boolean);
  // ISO dates sort as text, so the earliest is the smallest string.
  return dates.length ? dates.reduce((first, date) => (date < first ? date : first)) : null;
}

export function tenantLeaseExpiry(tenant: {
  occupancies?: Occupancy[];
}): LeaseExpiry {
  const known = (tenant.occupancies || [])
    .map((occupancy) =>
      leaseExpiryFor(occupancy, occupancy.rentRenewalNotations || ""),
    )
    .filter(
      (expiry): expiry is Exclude<LeaseExpiry, { state: "unknown" }> =>
        expiry.state !== "unknown",
    );

  if (known.length === 0) return { state: "unknown" };

  // Expired outranks expiring outranks inside-the-year outranks running: the
  // row should carry the worst news the tenant has, not an average of it.
  const severity = { expired: 0, expiring: 1, withinYear: 2, ok: 3 } as const;
  const worst = Math.min(...known.map((expiry) => severity[expiry.state]));
  const contenders = known.filter((expiry) => severity[expiry.state] === worst);

  // Within a state: the longest overrun, or the nearest deadline.
  return contenders.reduce((chosen, expiry) =>
    expiry.state === "expired"
      ? expiry.days > chosen.days
        ? expiry
        : chosen
      : expiry.days < chosen.days
        ? expiry
        : chosen,
  );
}

// The occupancy holds the unit until the contract runs out — or until the date
// it was cut short, which is what a takeover sets. A lease with neither is
// open-ended, and therefore still active.
export function occupancyEndDate(schedule: LeaseSchedule): string | null {
  return schedule.leaseEndDate || dateUnderContract(schedule);
}

/**
 * What fraction of a calendar month this lease held a space, counted in days.
 *
 * Recoveries — taxes, BMVA dues and CAM — follow occupancy, and when a unit
 * changes hands mid-month they divide by the days each tenancy held it. Rent
 * does NOT: the lease states a monthly rent and a part month is charged whole,
 * which is why this is used by the recovery lines only.
 *
 * Both ends are inclusive, which is what makes a handover add up: the takeover
 * wizard ends the outgoing lease the day BEFORE the takeover date and commences
 * the incoming one ON it, so their two shares of that month come to exactly 1.
 *
 * `month` is 1-based. The boundary dates are compared as ISO text rather than as
 * Date objects — a bare "2026-03-01" read through Date is UTC midnight and can
 * land in February once a western timezone is applied.
 *
 * Returns 0 for a month the lease does not run in at all, and 1 for one it runs
 * through from the first to the last — including the case where it has no
 * commencement date recorded, since a lease with no start is not a lease that
 * started late.
 */
export function occupancyDayShare(
  occupancy: LeaseSchedule & Pick<Occupancy, "leaseCommenceDate">,
  space: Pick<LeasedSpace, "rentCommenceDate">,
  year: number,
  month: number,
): number {
  const first = `${year}-${pad(month)}-01`;
  const days = new Date(year, month, 0).getDate();
  const last = `${year}-${pad(month)}-${pad(days)}`;

  // The same rule the statement uses to decide whether a lease runs in a month,
  // so the two can never disagree about which months are charged — this only
  // settles how much of the boundary month each side bears.
  const commenced = space.rentCommenceDate || occupancy.leaseCommenceDate;
  const ended = occupancyEndDate(occupancy);

  const from = commenced && commenced > first ? commenced : first;
  const to = ended && ended < last ? ended : last;
  if (from > to) return 0;

  const dayOf = (iso: string): number => Number(iso.slice(8, 10));
  const held = dayOf(to) - dayOf(from) + 1;
  return held <= 0 ? 0 : Math.min(1, held / days);
}

// Everything the rent escalation is worked out from. Drafts on the Tenant form
// satisfy this shape too, so the card can show live figures before saving.
export interface RentEscalation {
  rentCommenceDate: string;
  leaseCommenceDate: string;
  rentPsfCommencement: number | null;
  /** Renewals that restart the calculation from their own date. */
  rentResets: RentReset[];
  minimumIncrease: number | null;
  maximumIncrease: number | null;
  rentStepUpMonth: RentStepUpMonth | "";
}

/** One commencement of rent: where the escalation starts, and from when. */
export interface RentBasis {
  rentCommenceDate: string;
  rentPsfCommencement: number | null;
}

// Which commencement governs a given date: the latest renewal that has taken
// effect by then, or the original lease if none has. Asking "as of when" is
// what keeps history intact — a renewal never reaches back past its own date.
export function rentBasisAsOf(escalation: RentEscalation, asOf: string): RentBasis {
  const effective = (escalation.rentResets || [])
    .filter((reset) => reset.rentCommenceDate && reset.rentCommenceDate <= asOf)
    .sort((a, b) => a.rentCommenceDate.localeCompare(b.rentCommenceDate));
  const latest = effective[effective.length - 1];
  return latest
    ? {
        rentCommenceDate: latest.rentCommenceDate,
        rentPsfCommencement: latest.rentPsfCommencement,
      }
    : {
        rentCommenceDate: escalation.rentCommenceDate || escalation.leaseCommenceDate,
        rentPsfCommencement: escalation.rentPsfCommencement,
      };
}

// The annual increase to apply. When the min and max differ the actual figure
// depends on a measure the record doesn't hold, so the contractual floor —
// the minimum — is used.
export function annualIncreaseRate(escalation: RentEscalation): number | null {
  const rate = escalation.minimumIncrease ?? escalation.maximumIncrease;
  return rate === null || rate === undefined ? null : rate / 100;
}

// Rent steps up on the 1st of the step-up month every year, the first one
// falling in the first such month after the governing rent commencement —
// the original lease's, or a renewal's once that has taken effect.
function firstStepUp(
  escalation: RentEscalation,
  basis: RentBasis,
): { monthIndex: number; year: number } | null {
  const monthIndex = RENT_STEP_UP_MONTHS.indexOf(escalation.rentStepUpMonth as RentStepUpMonth);
  const [year, month, day] = (basis.rentCommenceDate || "").split("-").map(Number);
  if (monthIndex < 0 || !year || !month || !day) return null;

  const commenced = new Date(year, month - 1, day);
  const inCommenceYear = new Date(year, monthIndex, 1);
  return { monthIndex, year: inCommenceYear > commenced ? year : year + 1 };
}

// Step-ups in the years before this one. Deliberately ignores whether this
// year's step-up month has passed: the before/after pair is "this calendar
// year", so it holds steady all year and only moves on 1 January.
function stepUpsBeforeThisYear(escalation: RentEscalation, basis: RentBasis): number | null {
  const first = firstStepUp(escalation, basis);
  if (!first) return null;
  return Math.max(0, new Date().getFullYear() - first.year);
}

// Step-ups that had actually happened by `asOf`, that year's included once it
// lands. Counted against the date asked for rather than against today, so
// asking what was payable on a past date gives that date's answer.
function stepUpsToDate(
  escalation: RentEscalation,
  basis: RentBasis,
  asOf: string,
): number | null {
  const first = firstStepUp(escalation, basis);
  if (!first) return null;
  const [year, month, day] = (asOf || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  const on = new Date(year, month - 1, day);
  const before = Math.max(0, year - first.year);
  const thatYears = new Date(year, first.monthIndex, 1);
  const happenedThatYear = year >= first.year && on >= thatYears;
  return before + (happenedThatYear ? 1 : 0);
}

// The rate in force on a given date: the governing commencement rate,
// compounded by each step-up since that commencement. Asking for a date before
// a renewal returns the original lease's figure, which is what keeps past
// calculations unchanged when a renewal is added.
export function rentPsfOn(escalation: RentEscalation, asOf: string): number | null {
  const basis = rentBasisAsOf(escalation, asOf);
  const rate = annualIncreaseRate(escalation);
  // A rent that doesn't escalate never moves off its commencement rate, so
  // there is nothing to count and no step-up month to need. Without this a
  // flat rent reads as unknown rather than as flat.
  if (rate === 0) return basis.rentPsfCommencement;
  const elapsed = stepUpsToDate(escalation, basis, asOf);
  if (basis.rentPsfCommencement === null || rate === null || elapsed === null) return null;
  return basis.rentPsfCommencement * Math.pow(1 + rate, elapsed);
}

/** The rate actually in force today — this year's step-up counted once past. */
export function rentPsfToday(escalation: RentEscalation): number | null {
  return rentPsfOn(escalation, todayISO());
}

// True when the commencement governing today began during this calendar year
// AND this year's step-up month had already passed by then — so the year's
// escalation happened under the PREVIOUS rate, and this commencement is itself
// the year's rate change.
//
// Without this, a lease renewed after the step-up month reads as if it were
// due another increase in the same year: the negotiated rate lands in the
// pre-step box and a step-up that has already been taken gets applied on top.
//
// The converse is deliberate and confirmed: when the step-up month still falls
// AFTER the new commencement, that step-up does apply, however short the gap —
// a March commencement with an April step-up steps up in April. Only a
// commencement that has already missed the year's step-up supersedes it.
function commencementSupersedesThisYearsStepUp(
  escalation: RentEscalation,
  basis: RentBasis,
): boolean {
  const first = firstStepUp(escalation, basis);
  if (!first) return false;
  const thisYear = new Date().getFullYear();
  const [commencedYear] = (basis.rentCommenceDate || "").split("-").map(Number);
  // firstStepUp lands next year precisely when the step-up month has already
  // gone by in the commencement year.
  return commencedYear === thisYear && first.year > thisYear;
}

/** This calendar year's rate before its step-up. */
export function rentPsfCurrent(escalation: RentEscalation): number | null {
  const basis = rentBasisAsOf(escalation, todayISO());
  const rate = annualIncreaseRate(escalation);
  if (rate === 0) return basis.rentPsfCommencement;

  // The rate that was actually being paid before this commencement took over.
  // For a renewal that's the outgoing lease's figure for this year, step-up
  // included — which is what "before" means once the renewal is the change.
  if (commencementSupersedesThisYearsStepUp(escalation, basis)) {
    const previous = rentPsfOn(escalation, dayBefore(basis.rentCommenceDate));
    if (previous !== null) return previous;
  }

  const elapsed = stepUpsBeforeThisYear(escalation, basis);
  if (basis.rentPsfCommencement === null || rate === null || elapsed === null) return null;
  return basis.rentPsfCommencement * Math.pow(1 + rate, elapsed);
}

/** This calendar year's rate from its step-up month onward. */
export function rentPsfStepUp(escalation: RentEscalation): number | null {
  const basis = rentBasisAsOf(escalation, todayISO());
  const rate = annualIncreaseRate(escalation);
  if (rate === 0) return basis.rentPsfCommencement;

  // This year's increase is already spent, so the negotiated rate stands as
  // the post-step figure rather than being escalated again.
  if (commencementSupersedesThisYearsStepUp(escalation, basis)) {
    return basis.rentPsfCommencement;
  }

  const current = rentPsfCurrent(escalation);
  if (current === null || rate === null) return null;
  return current * (1 + rate);
}

// The sales figure at which percentage rent starts to apply: the annual
// minimum rent for the space (area × rate) divided by the breakpoint rate.
// Null whenever any input is missing, or the rate is zero — there's no
// breakpoint to reach.
export function percentageRentThreshold(
  occupancySqFt: number | null | undefined,
  rentPsf: number | null,
  breakpointRate: number | null,
): number | null {
  if (occupancySqFt === null || occupancySqFt === undefined) return null;
  if (rentPsf === null || breakpointRate === null || breakpointRate === 0) return null;
  return (occupancySqFt * rentPsf) / (breakpointRate / 100);
}

/** One stretch of a calendar year over which the rent rate doesn't change. */
export interface BreakpointPeriod {
  /** ISO, inclusive. */
  start: string;
  /** ISO, inclusive. */
  end: string;
  days: number;
  rentPsf: number | null;
  /** Area × rate: what the space earns over a full year at this rate. */
  annualMinimumRent: number | null;
  /** That annual rent divided by the breakpoint rate. */
  naturalBreakpoint: number | null;
}

export interface NaturalBreakpointYear {
  year: number;
  daysInYear: number;
  breakpointRate: number | null;
  /** In date order. Empty when no rent runs in the year at all. */
  periods: BreakpointPeriod[];
  /** The year's breakpoint, or null if any period's is unknown. */
  total: number | null;
}

const dateFromISO = (iso: string): Date | null => {
  const [year, month, day] = (iso || "").split("-").map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
};

// Inclusive day count. Rounded because a period spanning a daylight-saving
// change is 23 or 25 hours long, not 24.
const dayCount = (startISO: string, endISO: string): number => {
  const start = dateFromISO(startISO);
  const end = dateFromISO(endISO);
  if (!start || !end) return 0;
  return Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
};

// The natural breakpoint for a calendar year, broken into the periods it's
// worked out over.
//
// The breakpoint isn't one figure for the year whenever the rent changes
// mid-year — and it changes at every step-up and at every renewal. So the year
// is cut at each of those dates, each period gets the annual breakpoint at the
// rate then in force, and the year's figure is those annual breakpoints over
// the days each is actually in force: a rate that holds all year carries its
// whole breakpoint, one that holds half the year carries half of it.
//
// The year is also clipped to the lease itself. Rent commencing in July earns
// no breakpoint in January, so only the part of the year the lease is running
// counts.
export function naturalBreakpointYear(
  escalation: RentEscalation,
  schedule: LeaseSchedule,
  occupancySqFt: number | null | undefined,
  breakpointRate: number | null,
  year: number = new Date().getFullYear(),
): NaturalBreakpointYear {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const daysInYear = dayCount(yearStart, yearEnd);
  const empty: NaturalBreakpointYear = {
    year,
    daysInYear,
    breakpointRate,
    periods: [],
    total: null,
  };

  const commenced = escalation.rentCommenceDate || escalation.leaseCommenceDate;
  const start = commenced && commenced > yearStart ? commenced : yearStart;
  const ended = occupancyEndDate(schedule);
  const end = ended && ended < yearEnd ? ended : yearEnd;
  if (!commenced || start > end) return empty;

  // A period opens wherever the rate can move: at each renewal that lands in
  // the year, and at the annual step-up.
  const boundaries = new Set<string>([start]);
  for (const reset of escalation.rentResets || []) {
    const date = reset.rentCommenceDate;
    if (date && date > start && date <= end) boundaries.add(date);
  }
  const stepUpIndex = RENT_STEP_UP_MONTHS.indexOf(escalation.rentStepUpMonth as RentStepUpMonth);
  if (stepUpIndex >= 0) {
    const stepUp = `${year}-${pad(stepUpIndex + 1)}-01`;
    if (stepUp > start && stepUp <= end) boundaries.add(stepUp);
  }

  const starts = [...boundaries].sort();
  const periods: BreakpointPeriod[] = starts.map((periodStart, index) => {
    const periodEnd = index + 1 < starts.length ? dayBefore(starts[index + 1]) : end;
    // The rate in force on the day the period opens — which is what it stays
    // at, since a period only ends where the rate changes.
    const rentPsf = rentPsfOn(escalation, periodStart);
    const annualMinimumRent =
      rentPsf === null || occupancySqFt === null || occupancySqFt === undefined
        ? null
        : occupancySqFt * rentPsf;
    return {
      start: periodStart,
      end: periodEnd,
      days: dayCount(periodStart, periodEnd),
      rentPsf,
      annualMinimumRent,
      naturalBreakpoint: percentageRentThreshold(occupancySqFt, rentPsf, breakpointRate),
    };
  });

  const total = periods.some((period) => period.naturalBreakpoint === null)
    ? null
    : periods.reduce(
        (sum, period) => sum + (period.naturalBreakpoint as number) * (period.days / daysInYear),
        0,
      );

  return { year, daysInYear, breakpointRate, periods, total };
}

/** One leased space's contribution to its lease's breakpoint. */
export interface LeaseBreakpointLine {
  spaceId: string;
  /** The space as the reviewer named it, or the unit it points at. */
  label: string;
  breakdown: NaturalBreakpointYear;
}

export interface LeaseBreakpoint {
  /** Only the spaces that actually carry one, in the order the lease lists them. */
  lines: LeaseBreakpointLine[];
  /** Their sum, or null when no space carries one. */
  total: number | null;
}

/**
 * A lease's natural breakpoint for the year, and the spaces it's made of.
 *
 * The rate is the agreement's and the area is each space's, so the figure is
 * built space by space and added up. A space is skipped entirely when its
 * Space Type doesn't attract percentage rent, when it has no unit to take an
 * area from, or when its own rent isn't recorded yet — all three are "no
 * breakpoint here", which is null rather than zero: a $0 breakpoint would read
 * as every dollar of sales being over the line.
 */
export function occupancyBreakpointYear(
  occupancy: Occupancy,
  unitById: (unitId: string) => Unit | undefined,
  configuration: Pick<TospConfiguration, "percentageRentSpaceTypes">,
  year: number = new Date().getFullYear(),
): LeaseBreakpoint {
  const lines: LeaseBreakpointLine[] = [];
  let total: number | null = null;

  for (const space of occupancy.leasedSpaces || []) {
    const unit = space.unitId ? unitById(space.unitId) : undefined;
    if (!chargesPercentageRent(configuration, unit)) continue;

    const breakdown = naturalBreakpointYear(
      spaceEscalation(occupancy, space),
      occupancy,
      unit?.occupancySqFt,
      occupancy.percentageRentThresholdRate,
      year,
    );
    if (breakdown.total === null) continue;

    total = (total ?? 0) + breakdown.total;
    lines.push({ spaceId: space.id, label: leasedSpaceLabel(space, unit), breakdown });
  }

  return { lines, total };
}

/**
 * How a leased space is named wherever one label is wanted.
 *
 * What the lease calls it wins — that's the name the tenant and the property
 * manager both use. A space read forward from the older shape has no name yet,
 * so it shows as the unit it points at, and an unassigned one with no name is
 * still something rather than a blank row.
 */
export const leasedSpaceLabel = (
  space: Pick<LeasedSpace, "name" | "unitId">,
  unit?: Pick<Unit, "suiteNumber" | "spaceType">,
): string =>
  space.name?.trim() ||
  [unit?.suiteNumber, unit?.spaceType].filter(Boolean).join(" · ") ||
  "Unassigned space";

/**
 * Whether this lease still holds its units.
 *
 * Only an EXPLICIT end releases them. `leaseEndDate` is set when a lease is
 * cut short — a takeover, or a tenant who left — and is therefore a decision
 * somebody made. A term that has simply run out is not: the tenant is still in
 * the premises, rent is still arriving, and the renewal is sitting on
 * somebody's desk. Releasing the unit there would offer it to a second tenant
 * while the first is still trading out of it.
 *
 * So a lapsed term is a HOLDOVER, not a vacancy. The Lease column still shows
 * it in red and counts the days since it ended — the flag is what says the
 * paperwork is overdue; this only says who is in the unit.
 *
 * Open-ended terms were always active, and still are.
 */
export function isOccupancyActive(schedule: LeaseSchedule): boolean {
  // An end date of TODAY releases the unit today, not tomorrow.
  //
  // Was `>=`, which held the unit through its last day. Strictly that is what a
  // lease does — it is in force on the date it ends — but the end date is only
  // ever written by somebody stating that the tenancy is over: a takeover being
  // agreed, or an admin confirming a tenant has left. Having answered that, they
  // expect the unit to be free and the tenancy filed, and being told to come
  // back tomorrow reads as the answer not having registered.
  //
  // This governs only leases carrying an EXPLICIT end date. A term that has
  // merely run out still holds its unit — see the holdover reasoning above — so
  // no lease changes hands a day early because of this.
  if (schedule.leaseEndDate) return schedule.leaseEndDate > todayISO();
  return true;
}

// Who currently holds a unit, under which lease, and through which of that
// lease's spaces — what a takeover needs, since it has to end that specific
// lease. `excludeTenantId` lets the tenant being edited ignore its own leases.
//
// A lease is active or ended as a whole: the term, not any one space, is what
// runs out. So the space identifies which area was taken over, while the
// occupancy is what a takeover actually ends.
export function activeOccupancyOf(
  unitId: string,
  tenants: Tenant[],
  excludeTenantId?: string,
): { tenant: Tenant; occupancy: Occupancy; space: LeasedSpace } | undefined {
  for (const tenant of tenants) {
    if (tenant.id === excludeTenantId) continue;
    for (const occupancy of tenant.occupancies || []) {
      if (!isOccupancyActive(occupancy)) continue;
      const space = (occupancy.leasedSpaces || []).find((s) => s.unitId === unitId);
      if (space) return { tenant, occupancy, space };
    }
  }
  return undefined;
}

/**
 * Whether this tenancy is over.
 *
 * A tenant record IS a tenancy — one business, one lease, its own SMRT Docs
 * service — so a contact running two businesses holds two records and each
 * ends on its own. That is why this asks about a record rather than a person:
 * handing over one shop does not close the other.
 *
 * Derived, never stored. The end dates already say it, and a second flag beside
 * them could disagree with them — re-dating a lease would leave the flag behind.
 *
 * A record with NO leases at all is not past: that is a tenancy part-way
 * through being set up, which needs finishing rather than filing away.
 */
export function isPastTenancy(tenant: Pick<Tenant, "occupancies">): boolean {
  const leases = tenant.occupancies || [];
  return leases.length > 0 && !leases.some(isOccupancyActive);
}

/**
 * A tenancy whose lease has run out and which nobody has decided about yet.
 *
 * A lapsed term does NOT release the unit on its own — see `isOccupancyActive`:
 * the tenant is still in the premises and the renewal may be on somebody's desk,
 * so only an explicit end date lets go. That is the right default, and it is also
 * how a tenancy can quietly stay "current" for ever after the business has gone.
 *
 * So each of these is a question for the admin: have they left, or is the
 * paperwork late? Oldest first — the longest overrun is the most pressing.
 *
 * The lease's last day comes back with it, because that is what the exit date
 * becomes when the answer is "they have gone": the tenancy ended when its term
 * did, not on the day somebody got round to recording it.
 */
export interface LapsedTenancy {
  tenant: Tenant;
  occupancy: Occupancy;
  /** The lease's last day, ISO. What the exit date is set to. */
  endedOn: string;
  /** 0 when today IS the last day, higher once it has gone by. */
  daysOver: number;
}

export function lapsedTenancies(tenants: Tenant[]): LapsedTenancy[] {
  const today = todayISO();
  const found: LapsedTenancy[] = [];

  for (const tenant of tenants) {
    // Already ended, so there is nothing to ask — it is a past tenancy.
    if (isPastTenancy(tenant)) continue;

    for (const occupancy of tenant.occupancies || []) {
      // An explicit end date IS the decision, whichever way it went.
      if (occupancy.leaseEndDate) continue;

      const expiry = leaseExpiryFor(occupancy, occupancy.rentRenewalNotations || "");
      if (expiry.state === "unknown") continue;

      // Today counts. A lease whose last day is today has nothing left to run,
      // and leaving it until tomorrow means a day of a tenancy nobody has
      // decided about — which is the state this exists to clear.
      if (expiry.on > today) continue;

      found.push({
        tenant,
        occupancy,
        endedOn: expiry.on,
        daysOver: daysBetween(expiry.on, today) ?? 0,
      });
    }
  }

  return found.sort((a, b) => a.endedOn.localeCompare(b.endedOn));
}

/** The businesses still trading — every list but the Past Tenants tab. */
export const currentTenants = <T extends Pick<Tenant, "occupancies">>(tenants: T[]): T[] =>
  tenants.filter((tenant) => !isPastTenancy(tenant));

/** The businesses that have left. */
export const pastTenants = <T extends Pick<Tenant, "occupancies">>(tenants: T[]): T[] =>
  tenants.filter(isPastTenancy);

/**
 * The day this tenancy ended — the latest end date across its leases, or null
 * while any of them still runs.
 *
 * The LATEST, because a tenancy holding a shop and a locker is over when the
 * last of them goes, and because a takeover ends a lease as a whole.
 */
export function tenancyEndDate(tenant: Pick<Tenant, "occupancies">): string | null {
  const leases = tenant.occupancies || [];
  if (!isPastTenancy(tenant)) return null;
  const ends = leases.map((occupancy) => occupancy.leaseEndDate || "").filter(Boolean);
  // ISO dates sort as text, so the latest is the largest string.
  return ends.length ? ends.reduce((last, date) => (date > last ? date : last)) : null;
}

/** Whether any lease still demises this unit — the unit-delete guard. */
export const unitIsLeased = (unitId: string, tenants: Tenant[]): boolean =>
  tenants.some((tenant) =>
    (tenant.occupancies || []).some((occupancy) =>
      (occupancy.leasedSpaces || []).some((space) => space.unitId === unitId),
    ),
  );

/** The tenant currently holding a unit, if any. */
export function activeOccupant(
  unitId: string,
  tenants: Tenant[],
  excludeTenantId?: string,
): Tenant | undefined {
  return activeOccupancyOf(unitId, tenants, excludeTenantId)?.tenant;
}

// Same id shape the other modules use (SiteCheck / SMRT COP), so ids stay
// sortable by creation time and readable in logs.
export function newId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
